import React, { useEffect, useMemo, useState, useRef } from "react";
import { DateTime } from "luxon";
import { isCharacterAvailable, getRecurringRaidWindows, getSessionWindows } from "./heuristic_roster";
import { useTimezone } from "./lib/TimezoneContext";
import { bitIdxToUtcInstant, getCurrentResetWeek, getResetWeekForDate, localCellToBitIdx, pickerPartsToUtc, resetInstantForWeek, shiftWallClock } from "./lib/time";
import { formatWeekRange, ruleForRegionName } from "./lib/regionReset";
import { PickerTzLabel } from "./components/PickerTzLabel";
import { ThemeProvider, useTheme } from "./theme/ThemeProvider";

// --- CONSTANTS ---
const AUTH_BASE_URL = "";

const CLASS_COLORS: Record<string, string> = {
  Warrior: "#C79C6E", Paladin: "#F58CBA", Hunter: "#ABD473", Rogue: "#FFF569",
  Priest: "#FFFFFF", "Death Knight": "#C41F3B", Shaman: "#0070DE", Mage: "#40C7EB",
  Warlock: "#8787ED", Monk: "#00FF96", Druid: "#FF7D0A", "Demon Hunter": "#A330C9", Evoker: "#33937F",
};

// --- TYPES ---

export type RaidSize = 10 | 25 | 30 | 40;

export type CreateRaidGuild = {
  id: string;
  name: string;
  tag?: string | null;
  serverName?: string | null;
  region?: "EU" | "US" | null;
  logoUrl?: string | null;
  gameId: string;
};

type Game = {
  id: string;
  code: string;
  name: string;
};

export type CreateRaidCharacter = {
  id: string;
  name: string;
  className?: string;
  primaryRole?: string;
  guildId?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export type DayPattern = {
    start: string; // "20:00"
    end: string;   // "23:00"
};

export type SingleSessionConfig = {
    date: string;      // "2023-10-27"
    startTime: string; // "20:00"
    endTime: string;   // "23:00"
};

export type CreateRaidFormValues = {
  guildId: string;
  gameId: string;
  raidOptionId?: number | null;
  raidLabel: string;
  size: RaidSize;
  region: "EU" | "US";
  realm: string;
  
  sessions?: { startsAt: Date; durationMinutes: number }[];

  raidLeaderCharacterId: string | null;
  lootMasterCharacterId: string | null;
  metadata: Record<string, any>;
  
  isRecurring?: boolean;
  recurringPatterns?: Record<string, DayPattern>;
  
  softReserveEnabled?: boolean;
  maxSoftReserves?: number;
  chainedRaidOptionIds?: number[];
  publish?: boolean;
};

interface FieldOption {
  id: number;
  key: string;
  label: string;
  metadata: any;
  image_url?: string | null;
}

interface FieldGroup {
  id: number;
  key: string;
  label: string;
  multiSelect: boolean;
  isRequired: boolean;
  options: FieldOption[];
}

type CreateRaidPageProps = {
  guilds: CreateRaidGuild[];
  characters: CreateRaidCharacter[];
  accountAvailability: boolean[];
  onCancel: () => void;
  onSubmit: (values: CreateRaidFormValues) => void | Promise<void>;
  submitting: boolean;
  error: string | null;
  me?: any | null;
};

interface CompositionTag {
  id: number;
  tag_key: string;
  tag_label: string;
  tag_category: string;
  tag_subcategory: string | null;
  description: string | null;
  sort_order: number;
}

const STEP_CONFIG = [
  { id: 1, label: "Setup", desc: "Guild & Time" },
  { id: 2, label: "Roster", desc: "Assignments" },
  { id: 3, label: "Review", desc: "Confirm" },
] as const;

// --- DRAG TYPES (Restored) ---

type BenchDragOrigin = {
  type: "bench";
  charId: string;
  roleLabel: string | null;
};

type SlotDragOrigin = {
  type: "slot";
  charId: string;
  slotIndex: number;
  roleLabel: string | null;
};

// A card dragged FROM the true bench (backup zone). Distinct from "bench",
// which confusingly refers to the available-players pool in this file.
type BenchSeatDragOrigin = {
  type: "benchseat";
  charId: string;
  roleLabel: string | null;
};

type DragOrigin = BenchDragOrigin | SlotDragOrigin | BenchSeatDragOrigin | null;

// --- SQUAD TYPES ---
interface SquadInfo {
  squadId: string;
  squadName: string;
  squadSize: number;
  memberUserIds: string[];
}

type SquadLookup = Record<string, SquadInfo>; // keyed by userId

// --- UTILS ---

function formatTime(totalMinutes: number): string {
  if (totalMinutes > 24 * 60) totalMinutes = 24 * 60;
  if (totalMinutes < 0) totalMinutes = 0;
  const renderMinutes = totalMinutes === 24 * 60 ? 0 : totalMinutes;
  const outH = Math.floor(renderMinutes / 60).toString().padStart(2, "0");
  const outM = (renderMinutes % 60).toString().padStart(2, "0");
  return `${outH}:${outM}`;
}

function snapToHalfHour(time: string): string {
  const [hStr, mStr] = time.split(":");
  let total = (Number(hStr) || 0) * 60 + (Number(mStr) || 0);
  total = Math.round(total / 30) * 30;
  return formatTime(total);
}

function adjustTime(timeStr: string, minutes: number): string {
    const [hStr, mStr] = timeStr.split(":");
    let total = (Number(hStr) || 0) * 60 + (Number(mStr) || 0) + minutes;
    const max = 24 * 60; // 1440 minutes in a day
    // Wrap around instead of clamping
    total = ((total % max) + max) % max;
    return formatTime(total);
}

function softBgFromHex(color: string, alpha = 0.18): string | undefined {
  const m = color.trim().match(/^#?([0-9a-fA-F]{6})$/);
  if (!m) return undefined;
  const hex = m[1];
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/*
 * Warcraft Logs parse brackets. These are the canonical WCL colors — players
 * read the bracket by color, so the hues are not ours to change. They're all
 * rendered as text though, and WCL picks them for a dark site, so on a light
 * palette they go through onLight() to stay legible. The adjuster is passed
 * in rather than imported: it must be the theme-aware one, which is identity
 * on dark palettes where these colors are already correct.
 */
const PARSE_COLORS = {
  legendary: "#e5cc80", // 100
  astounding: "#e268a8", // 99+
  epic: "#ff8000", // 95+
  rare: "#a335ee", // 75+
  uncommon: "#0070dd", // 50+
  common: "#1eff00", // 25+
  poor: "#9d9d9d", // below 25
} as const;

function getParseColor(score: number | undefined, onLight: (hex: string) => string): string {
  if (score === undefined) return "var(--c-ink-4)";
  if (score === 100) return onLight(PARSE_COLORS.legendary);
  if (score >= 99) return onLight(PARSE_COLORS.astounding);
  if (score >= 95) return onLight(PARSE_COLORS.epic);
  if (score >= 75) return onLight(PARSE_COLORS.rare);
  if (score >= 50) return onLight(PARSE_COLORS.uncommon);
  if (score >= 25) return onLight(PARSE_COLORS.common);
  return onLight(PARSE_COLORS.poor);
}

function getPrimaryRoleColorForChar(c: CreateRaidCharacter | any, fields: FieldGroup[] | null | undefined): string | null {
  if (!fields) return null;
  const roleGroup = fields.find((g) => g.key === "role");
  if (!roleGroup) return null;
  const rawOptions: any[] = (c as any).options ?? [];
  const entry = rawOptions.find((o) => o && Number(o.group_id) === Number(roleGroup.id));
  if (!entry) return null;
  const opt = roleGroup.options.find((fo) => Number(fo.id) === Number(entry.option_id));
  const meta = (opt && opt.metadata) || {};
  return typeof meta.role_color === "string" ? meta.role_color.trim() : null;
}

function getRoleColorForRoleLabel(c: CreateRaidCharacter | any, fields: FieldGroup[] | null | undefined, roleLabel: string): string | null {
  if (!fields || !roleLabel) return null;
  const roleGroup = fields.find((g) => g.key === "role");
  if (!roleGroup || !Array.isArray((c as any).options)) return null;
  const rawOptions: any[] = (c as any).options ?? [];
  const target = roleLabel.trim().toLowerCase();
  let chosen: FieldOption | null = null;
  for (const entry of rawOptions) {
    if (!entry || Number(entry.group_id) !== Number(roleGroup.id)) continue;
    const opt = roleGroup.options.find((fo) => Number(fo.id) === Number(entry.option_id));
    if (!opt) continue;
    const lbl = (opt.label || "").trim();
    if (!chosen) chosen = opt; 
    if (lbl.toLowerCase() === target) { chosen = opt; break; }
  }
  if (!chosen) return null;
  const meta = chosen.metadata || {};
  return typeof meta.role_color === "string" ? meta.role_color.trim() : null;
}

function resolveRoleLabels(c: CreateRaidCharacter | any, fields: FieldGroup[] | null | undefined): string[] {
  const primaryRole = ((c as any).primaryRole ?? (c as any).primary_role ?? "") as string;
  const trimmedPrimary = primaryRole ? primaryRole.trim() : "";
  if (!fields || !Array.isArray((c as any).options)) return trimmedPrimary ? [trimmedPrimary] : [];
  const roleGroup = fields.find((g) => g.key === "role");
  if (!roleGroup) return trimmedPrimary ? [trimmedPrimary] : [];
  const rawOptions: any[] = (c as any).options ?? [];
  let labels = rawOptions
    .filter((o) => o && Number(o.group_id) === Number(roleGroup.id))
    .map((o) => {
      const opt = roleGroup.options.find((fo) => Number(fo.id) === Number(o.option_id));
      return (opt?.label ?? "").trim();
    })
    .filter((lbl) => lbl.length > 0);
  if (!labels.length) return trimmedPrimary ? [trimmedPrimary] : [];
  if (trimmedPrimary) {
    // Match by value_key, not label - primary_role stores keys like "ranged_dps"
    const normKey = trimmedPrimary.toLowerCase().replace(/\s+/g, '_');
    const matchIdx = rawOptions
      .filter((o) => o && Number(o.group_id) === Number(roleGroup.id))
      .findIndex((o) => {
        const opt = roleGroup.options.find((fo) => Number(fo.id) === Number(o.option_id));
        return (opt?.key ?? "").toLowerCase() === normKey;
      });
    if (matchIdx > 0 && matchIdx < labels.length) {
      const copy = labels.slice();
      const [p] = copy.splice(matchIdx, 1);
      labels = [p, ...copy];
    }
    // Don't add trimmedPrimary if no match - labels from options are authoritative
  }
  return labels;
}

// Resolve spec entries for a character: returns [{specLabel, roleLabel, roleKey}]
function resolveSpecEntries(c: CreateRaidCharacter | any, fields: FieldGroup[] | null | undefined): { specLabel: string; roleLabel: string; specKey: string }[] {
  if (!fields || !Array.isArray((c as any).options)) {
    // Fallback to metadata if available
    const meta = (c as any).metadata || {};
    const specLabels: string[] = meta.spec_labels || [];
    const roleLabels: string[] = meta.role_labels || [];
    if (specLabels.length > 0) {
      return specLabels.map((sl: string, i: number) => ({
        specLabel: sl,
        roleLabel: roleLabels[i] || "DPS",
        specKey: (meta.spec_keys || [])[i] || sl.toLowerCase(),
      }));
    }
    // Ultimate fallback: use resolveRoleLabels
    const roles = resolveRoleLabels(c, fields);
    return roles.map(r => ({ specLabel: r, roleLabel: r, specKey: r.toLowerCase().replace(/\s+/g, '_') }));
  }
  const specGroup = fields.find((g) => g.key === "spec");
  const roleGroup = fields.find((g) => g.key === "role");
  if (!specGroup) {
    // No spec group in game fields - fall back to roles
    const roles = resolveRoleLabels(c, fields);
    return roles.map(r => ({ specLabel: r, roleLabel: r, specKey: r.toLowerCase().replace(/\s+/g, '_') }));
  }
  const ROLE_LABEL_MAP: Record<string, string> = { 'tank': 'Tank', 'healer': 'Healer', 'melee_dps': 'Melee DPS', 'ranged_dps': 'Ranged DPS' };
  const rawOptions: any[] = (c as any).options ?? [];
  const entries = rawOptions
    .filter((o) => o && Number(o.group_id) === Number(specGroup.id))
    .map((o) => {
      const opt = specGroup.options.find((fo) => Number(fo.id) === Number(o.option_id));
      if (!opt) return null;
      const specLabel = (opt.label ?? "").trim();
      const roleKey = (opt.metadata?.role_key ?? "").trim();
      const roleLabel = ROLE_LABEL_MAP[roleKey] || roleKey || "DPS";
      const specKey = (opt.key ?? specLabel.toLowerCase().replace(/\s+/g, '_'));
      return specLabel ? { specLabel, roleLabel, specKey } : null;
    })
    .filter((e): e is { specLabel: string; roleLabel: string; specKey: string } => e !== null);
  if (entries.length > 0) return entries;
  // Fallback to metadata
  const meta = (c as any).metadata || {};
  const specLabels: string[] = meta.spec_labels || [];
  const roleLabels: string[] = meta.role_labels || [];
  if (specLabels.length > 0) {
    return specLabels.map((sl: string, i: number) => ({
      specLabel: sl,
      roleLabel: roleLabels[i] || "DPS",
      specKey: (meta.spec_keys || [])[i] || sl.toLowerCase(),
    }));
  }
  const roles = resolveRoleLabels(c, fields);
  return roles.map(r => ({ specLabel: r, roleLabel: r, specKey: r.toLowerCase().replace(/\s+/g, '_') }));
}

// Check if character is willing to raid lead
function isWillingRaidLead(c: CreateRaidCharacter | any, fields: FieldGroup[] | null | undefined): boolean {
  if (!fields || !Array.isArray((c as any).options)) return false;
  const prefGroup = fields.find((g) => g.key === "raid_preferences");
  if (!prefGroup) return false;
  const rawOptions: any[] = (c as any).options ?? [];
  return rawOptions.some((o) => {
    if (!o || Number(o.group_id) !== Number(prefGroup.id)) return false;
    const opt = prefGroup.options.find((fo) => Number(fo.id) === Number(o.option_id));
    return opt?.key === "willing_raid_lead";
  });
}

// Check if character is willing to be master looter
function isWillingLootMaster(c: CreateRaidCharacter | any, fields: FieldGroup[] | null | undefined): boolean {
  if (!fields || !Array.isArray((c as any).options)) return false;
  const prefGroup = fields.find((g) => g.key === "raid_preferences");
  if (!prefGroup) return false;
  const rawOptions: any[] = (c as any).options ?? [];
  return rawOptions.some((o) => {
    if (!o || Number(o.group_id) !== Number(prefGroup.id)) return false;
    const opt = prefGroup.options.find((fo) => Number(fo.id) === Number(o.option_id));
    return opt?.key === "willing_loot_master";
  });
}

function getOwnerKeyForChar(c: CreateRaidCharacter | any, me: any | null | undefined): string | null {
  if (!c) return null;
  if ((c as any).ownerDiscordId != null) return `discord:${(c as any).ownerDiscordId}`;
  if ((c as any).owner_discord_id != null) return `discord:${(c as any).owner_discord_id}`;
  if ((c as any).ownerId != null) return String((c as any).ownerId);
  if ((c as any).owner_id != null) return String((c as any).owner_id);
  if ((c as any).userId != null) return String((c as any).userId);
  if ((c as any).user_id != null) return String((c as any).user_id);
  return null;
}

// --- BUFF/COMPOSITION TAG COVERAGE ---

function getCharacterProvidedTags(c: CreateRaidCharacter | any, fields: FieldGroup[] | null | undefined, assignedRole: string | null): string[] {
  if (!fields) return [];
  const specGroup = fields.find((g) => g.key === "spec");
  if (!specGroup) return [];
  const rawOptions: any[] = (c as any).options ?? [];
  const specEntries = rawOptions.filter((o) => o && Number(o.group_id) === Number(specGroup.id));
  const allTags = new Set<string>();
  for (const entry of specEntries) {
    const opt = specGroup.options.find((fo) => Number(fo.id) === Number(entry.option_id));
    if (!opt) continue;
    const tags: string[] = opt.metadata?.provides_tags || [];
    if (assignedRole && specEntries.length > 1) {
      const roleKey = (opt.metadata?.role_key ?? "").trim();
      const ROLE_LABEL_MAP: Record<string, string> = { 'tank': 'Tank', 'healer': 'Healer', 'melee_dps': 'Melee DPS', 'ranged_dps': 'Ranged DPS' };
      if (ROLE_LABEL_MAP[roleKey] === assignedRole) {
        tags.forEach(t => allTags.add(t));
      }
    } else {
      tags.forEach(t => allTags.add(t));
    }
  }
  if (allTags.size === 0) {
    const meta = (c as any).metadata || {};
    const specKeys: string[] = meta.spec_keys || [];
    for (const sk of specKeys) {
      const opt = specGroup.options.find((fo) => fo.key === sk);
      if (opt?.metadata?.provides_tags) {
        (opt.metadata.provides_tags as string[]).forEach(t => allTags.add(t));
      }
    }
  }
  return Array.from(allTags);
}

type TagCoverage = {
  tag: CompositionTag;
  covered: boolean;
  providers: string[];
};

type CategoryCoverage = {
  category: string;
  subcategory: string | null;
  tags: TagCoverage[];
  coveredCount: number;
  totalCount: number;
};

function computeBuffCoverage(
  rosterSlots: (string | null)[],
  rosterRoles: (string | null)[],
  charById: Map<string, CreateRaidCharacter>,
  fields: FieldGroup[] | null | undefined,
  compositionTags: CompositionTag[]
): CategoryCoverage[] {
  if (!compositionTags.length || !fields) return [];
  const tagProviders = new Map<string, string[]>();
  rosterSlots.forEach((charId, idx) => {
    if (!charId) return;
    const char = charById.get(charId);
    if (!char) return;
    const role = rosterRoles[idx];
    const tags = getCharacterProvidedTags(char, fields, role);
    for (const t of tags) {
      if (!tagProviders.has(t)) tagProviders.set(t, []);
      tagProviders.get(t)!.push(char.name);
    }
  });
  const groupMap = new Map<string, { category: string; subcategory: string | null; tags: TagCoverage[] }>();
  for (const tag of compositionTags) {
    const key = `${tag.tag_category}|||${tag.tag_subcategory || ''}`;
    if (!groupMap.has(key)) {
      groupMap.set(key, { category: tag.tag_category, subcategory: tag.tag_subcategory, tags: [] });
    }
    const providers = tagProviders.get(tag.tag_key) || [];
    groupMap.get(key)!.tags.push({ tag, covered: providers.length > 0, providers });
  }
  return Array.from(groupMap.values()).map(g => ({
    ...g,
    coveredCount: g.tags.filter(t => t.covered).length,
    totalCount: g.tags.length,
  }));
}

const ROLE_ORDER = ["Tank", "Healer", "Melee DPS", "Ranged DPS"];

function chunkIntoGroups<T>(arr: T[], groupSize: number): T[][] {
  const groups: T[][] = [];
  for (let i = 0; i < arr.length; i += groupSize) {
    groups.push(arr.slice(i, i + groupSize));
  }
  return groups;
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
const SLOTS_PER_DAY = 48;
const TOTAL_AVAIL_SLOTS = DAYS.length * SLOTS_PER_DAY;

function getClassIconUrl(className: string | null | undefined, fields: FieldGroup[] | null | undefined): string | null {
  if (!className || !fields) return null;
  const classGroup = fields.find(g => g.key === 'class');
  if (!classGroup) return null;
  const opt = classGroup.options.find(o => o.label === className);
  return opt?.image_url || null;
}

function getSpecIconUrl(specLabel: string | null | undefined, className: string | null | undefined, fields: FieldGroup[] | null | undefined): string | null {
  if (!specLabel || !fields) return null;
  const specGroup = fields.find(g => g.key === 'spec');
  if (!specGroup) return null;
  const classKey = className?.toLowerCase().replace(/\s+/g, '') || '';
  const opt = specGroup.options.find(o => o.label === specLabel && o.metadata?.class_key === classKey);
  return opt?.image_url || null;
}

function getRoleIcon(role: string): string {
  switch (role?.trim()) {
    case "Tank": return "🛡️";
    case "Healer": return "💚";
    case "Melee DPS": return "⚔️";
    case "Ranged DPS": return "🏹";
    default: return "👤";
  }
}

function characterCardLines(c: CreateRaidCharacter | any, fields: FieldGroup[] | null | undefined, discordName: string | null | undefined): { line1: string; line2: string } {
  const name = (c as any).name ?? "";
  const displayDiscord = discordName && discordName.trim().length > 0 ? discordName.trim() : "";
  const line1 = displayDiscord ? `${name} (${displayDiscord})` : name;
  const rawClass = (c as any).className ?? (c as any).class_name ?? "";
  const cls = typeof rawClass === "string" ? rawClass.trim() : "";
  const roles = resolveRoleLabels(c, fields);
  const parts: string[] = [];
  if (cls) parts.push(cls);
  if (roles.length) parts.push(roles.join(" / "));
  const line2 = parts.join(" · ");
  return { line1, line2 };
}

// --- RosterCard Interface ---
interface RosterCardProps {
  character: CreateRaidCharacter | null;
  labelLines?: { line1: string; line2: string } | null;
  roleColor?: string | null;
  ownerDiscord?: string | null;
  isDragging?: boolean;
  isDisabled?: boolean;
  isSlot?: boolean;
  isDraggingFromHere?: boolean;
  highlightBg?: string; 
  isDuplicate?: boolean;
  isSameOwner?: boolean;
  displayRoles?: string[];
  displaySpec?: string | null;
  squadInfo?: SquadInfo | null;
  isSquadmateHovered?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  forwardRef?: React.Ref<HTMLDivElement>;
  classIconUrl?: string | null;
  wclScore?: number;
}

function RosterCard({ character, labelLines, roleColor, ownerDiscord, isDragging, isDisabled, isSlot, isDraggingFromHere, highlightBg, isDuplicate, isSameOwner, displayRoles, displaySpec, squadInfo, isSquadmateHovered, onClick, onRemove, onMouseDown, onMouseEnter, onMouseLeave, forwardRef, classIconUrl, wclScore }: RosterCardProps) {
  // Theme-aware legibility fixups for the runtime hex colors below (class,
  // role, parse). Identity on dark palettes — see ThemeProvider.
  const { onLight, onLightAccent } = useTheme();
  const BASE_CLS = "flex-none relative h-[52px] w-full flex items-stretch gap-2 rounded-lg border px-2.5 py-2 transition-all select-none duration-100";
  let stateCls = "";
  if (!character) {
    stateCls = "border-dashed border-[var(--c-line)] bg-[var(--c-sunken)] text-[var(--c-ink-4)]";
    if (isDraggingFromHere) stateCls += " opacity-40 border-[var(--c-accent-line)]";
  } else {
    if (isDisabled) {
      stateCls = "opacity-60 cursor-not-allowed border-[var(--c-line)] bg-[var(--c-surface)] grayscale";
    } else {
      stateCls = "border-[var(--c-line)] bg-[var(--c-surface)] cursor-grab hover:border-[var(--c-line-strong)] hover:bg-[var(--c-sunken-2)] active:cursor-grabbing hover:shadow-[var(--c-shadow-lg)]";
    }
    if (isDragging) stateCls += " opacity-0";
    if (isDraggingFromHere) stateCls += " opacity-30 border-dashed border-[var(--c-accent-line)]";
    if (isDuplicate) stateCls += " ring-2 ring-[var(--c-warn)] z-10 scale-[1.02]";
    else if (isSameOwner) stateCls += " ring-2 ring-[var(--c-accent)] shadow-[var(--c-glow-accent)]";
  }
  const style: React.CSSProperties = {};
  if (character && roleColor) style.borderColor = onLightAccent(roleColor);
  if (highlightBg && character) style.backgroundColor = highlightBg;
  if (isSquadmateHovered && character) {
    style.outline = '2px dashed var(--c-squad)';
    style.outlineOffset = '2px';
  }

  const primaryRole = displayRoles?.[0];

  return (
    <div ref={forwardRef} role={character && !isDisabled ? "button" : undefined} className={`${BASE_CLS} ${stateCls}`} style={style} onMouseDown={onMouseDown} onClick={!isDragging && !isDisabled ? onClick : undefined} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {/* Class icon (color bar fallback) */}
      {character && classIconUrl ? (
        <>
          <img src={classIconUrl} alt="" className="h-8 w-8 flex-shrink-0 rounded border border-[var(--c-line)] object-cover bg-[var(--c-sunken)]" onError={(e) => { const el = e.currentTarget as HTMLImageElement; el.style.display='none'; if (el.nextElementSibling) (el.nextElementSibling as HTMLElement).style.display='block'; }} />
          <div className="w-[3px] self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: roleColor ? onLightAccent(roleColor) : undefined, display: 'none' }} />
        </>
      ) : (
        <div className={`w-[3px] self-stretch rounded-full flex-shrink-0 ${character ? "" : "bg-[var(--c-sunken-2)]"}`} style={roleColor && character ? { backgroundColor: onLightAccent(roleColor) } : undefined} />
      )}
      
      {character ? (
        <>
          {/* Main content */}
          <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div className="flex items-center gap-1.5">
            <span className="truncate font-bold text-sm" style={{ color: onLight(CLASS_COLORS[(character as any).className || (character as any).class_name || ''] || '#9d9d9d') }}>{character.name}</span>
              {wclScore !== undefined && wclScore !== null && (
                <span
                  className="font-mono font-bold text-[13px] leading-none flex-shrink-0"
                  style={{ color: getParseColor(wclScore, onLight) }}
                  title={`Warcraft Logs parse: ${wclScore}`}
                >
                  {wclScore}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-[10px] text-[var(--c-ink-3)]">
              <span className="truncate">{(character as any).className || (character as any).class_name || "Unknown"}</span>
              {squadInfo && (
                <span className="flex items-center gap-0.5 text-[9px] text-[var(--c-squad-ink)] flex-shrink-0" title={squadInfo.squadName}>
                  <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  <span className="font-bold">{squadInfo.squadSize}</span>
                </span>
              )}
              {ownerDiscord && <><span className="opacity-30">·</span><span className="truncate">{ownerDiscord}</span></>}
            </div>
          </div>
          
          {/* Role pill on right */}
          {primaryRole && (
            <div className="flex items-center flex-shrink-0">
              <span 
                className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase"
                style={{ 
                  backgroundColor: roleColor ? `${onLightAccent(roleColor)}1f` : 'var(--c-sunken-2)',
                  color: roleColor ? onLight(roleColor) : 'var(--c-ink-2)'
                }}
              >
                {displaySpec || (primaryRole === "Melee DPS" ? "Melee" : primaryRole === "Ranged DPS" ? "Ranged" : primaryRole)}
              </span>
            </div>
          )}
          
          {/* Remove button for slots */}
          {isSlot && onRemove && (
            <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="flex h-5 w-5 items-center justify-center rounded text-[var(--c-ink-4)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] transition-colors text-xs">✕</button>
          )}
        </>
      ) : (
        <span className="flex-1 text-[10px] text-[var(--c-ink-4)] uppercase tracking-widest font-medium text-center flex items-center justify-center">Empty</span>
      )}
    </div>
  );
}

// --- SUGGESTED RAID TIMES ---

/** One suggested start, fully resolved for display and for the click handler. */
type RaidTimeSuggestion = {
  day: number;
  dayLabel: string;
  timeStr: string;
  available: number;
  total: number;
  pct: number;
  /** Local start, for comparing against the current selection. */
  startMillis: number;
  /** Pre-formatted so picking a time is pure state assignment, no date math. */
  date: string;
  startTime: string;
  endTime: string;
};

/*
 * Discriminated union rather than early-returned JSX, so the whole analysis
 * can live in a useMemo and the render stays a cheap switch over the result.
 */
type SuggestionState =
  | { kind: "loading" }
  | { kind: "no-signups" }
  | { kind: "empty"; isWeekEntirelyPast: boolean }
  | {
      kind: "ready";
      slots: RaidTimeSuggestion[];
      lockedOutCount: number;
      busyUserCount: number;
      unconfirmedCount: number;
      excludedCount: number;
    };

/** Window length for a suggested raid: 6 half-hour slots = 3 hours. */
const SUGGESTION_SLOTS = 6;

/*
 * Roster pool cards showing this many specs or fewer are half-width, so two sit
 * side by side. Three 44px tiles plus gaps is ~144px, which clears the ~165px
 * of inner width a half card has in the 450px pool column. Four would not fit,
 * so raising this without widening the column will overflow the tile row.
 */
const BENCH_PAIR_MAX_SPECS = 3;

/*
 * Rows a single pool column holds before the list splits into two columns.
 * A "row" is one wide card, or a pair of half-width ones.
 */
const BENCH_MAX_ROWS_PER_COLUMN = 6;

/*
 * THE `min-[1800px]:` BREAKPOINT, used by the pool panel and the column grid.
 *
 * The second pool column only exists where there is genuinely room for it.
 * Below 1800px the pool stays 450px and the two columns stack, so the reading
 * order is identical to a plain single-column list. Above it the pool widens to
 * 760px and the columns sit side by side.
 *
 * 1800px is the floor at which BOTH panels still work: the pool needs ~700px of
 * inner width for two columns that can each still split a card in half, and the
 * raid board needs the remaining ~1040px to keep four group columns at a usable
 * width. Lowering it starves the board.
 *
 * It is a literal rather than a constant because Tailwind scans source text for
 * complete class names and cannot resolve an interpolated one. The two places
 * that use it must be changed together.
 */

type SpecEntry = { charId: string; charName: string; className: string; specLabel: string; roleLabel: string; roleColor: string | null; char: CreateRaidCharacter };

type PlayerBenchEntry = {
  userId: string;
  displayName: string;
  primaryCharName: string;
  charCount: number;
  specs: SpecEntry[];
  squadInfo: SquadInfo | null;
  ownerKey: string | null;
};

/*
 * Pack bench entries into rows, then split into at most two columns.
 *
 * A wide entry takes a whole row; narrow ones pair up. Row count can't be read
 * off the item count because of that pairing, so the packing is simulated first
 * and the split is made by ROW, which keeps the columns level however the pairs
 * happened to fall.
 *
 * Shared by the active pool and the disabled sections so both lay out the same.
 */
function splitIntoBenchColumns<T>(items: T[], specCount: (item: T) => number): T[][] {
  const rows: T[][] = [];
  let pending: T | null = null;

  for (const item of items) {
    if (specCount(item) > BENCH_PAIR_MAX_SPECS) {
      // A wide entry takes a whole row, so flush any half-filled row first.
      if (pending) { rows.push([pending]); pending = null; }
      rows.push([item]);
    } else if (pending) {
      rows.push([pending, item]);
      pending = null;
    } else {
      pending = item;
    }
  }
  if (pending) rows.push([pending]);

  if (rows.length <= BENCH_MAX_ROWS_PER_COLUMN) return [items];
  const splitAt = Math.ceil(rows.length / 2);
  return [rows.slice(0, splitAt).flat(), rows.slice(splitAt).flat()];
}

/*
 * One of the greyed-out pool buckets: already seated, raid locked, busy, not
 * interested, unavailable.
 *
 * This exists as a single component because it used to be five copy-pasted
 * blocks, and they had drifted: three printed the character's ROLE where the
 * other two printed the spec, so the same player read "Melee" in one bucket and
 * "Fury" in another. One definition, one behaviour.
 */
function DisabledBenchSection({
  title,
  toneClass,
  dimClass,
  players,
}: {
  title: string;
  toneClass: string;
  dimClass: string;
  players: PlayerBenchEntry[];
}) {
  const { onLightAccent } = useTheme();
  if (players.length === 0) return null;

  // Ordered widest-first so the narrow cards stay contiguous and pair cleanly,
  // matching the active pool above.
  const ordered = [...players].sort(
    (a, b) => b.specs.length - a.specs.length || a.displayName.localeCompare(b.displayName)
  );
  const columns = splitIntoBenchColumns(ordered, (p) => p.specs.length);

  return (
    <div className="pt-6 border-t border-[var(--c-line)]">
      <div className={`mb-3 px-1 text-[10px] font-bold uppercase tracking-widest ${toneClass}`}>{title}</div>
      <div className={`grid gap-3 ${columns.length > 1 ? "grid-cols-1 min-[1800px]:grid-cols-2" : "grid-cols-1"} ${dimClass}`}>
        {columns.map((column, colIdx) => (
          <div key={colIdx} className="flex flex-wrap items-stretch content-start gap-2">
            {column.map((player) => (
              <div
                key={player.userId}
                className={`flex-none rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-3 ${
                  player.specs.length <= BENCH_PAIR_MAX_SPECS ? "w-[calc(50%-4px)]" : "w-full"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-bold text-[var(--c-ink)] truncate">
                    {player.primaryCharName}
                    {player.charCount > 1 && <span className="text-[var(--c-ink-3)] font-normal"> & {player.charCount - 1} more</span>}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {player.specs.map((spec) => (
                    <span
                      key={`${spec.charId}-${spec.specLabel}`}
                      className="flex min-w-0 max-w-full items-center gap-1 px-2 py-1 rounded-md border border-[var(--c-line)] bg-[var(--c-sunken)] text-[10px] text-[var(--c-ink-3)]"
                      title={`${spec.specLabel} · ${spec.charName} (${spec.className})`}
                    >
                      <span className="text-xs flex-shrink-0">{getRoleIcon(spec.roleLabel)}</span>
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: onLightAccent(CLASS_COLORS[spec.className] || '#9d9d9d') }} />
                      <span className="truncate">{spec.specLabel}</span>
                      <span className="text-[var(--c-ink-4)] truncate">{spec.charName}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// --- MAIN PAGE ---

/*
 * The page body. Wrapped by the default export below so that everything in
 * here (and RosterCard) can read the active palette from context.
 */
function CreateRaidPageInner({ guilds, characters, accountAvailability, onCancel, onSubmit, submitting, error, me }: CreateRaidPageProps) {
  const { themeClass, onLight, onLightAccent } = useTheme();
  const userTz = useTimezone();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const slotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const benchZoneRef = useRef<HTMLDivElement | null>(null);
  const [step, setStep] = useState<0 | 1 | 2>(0);

  // --- DATA LOADING & STATE ---
  const [games, setGames] = useState<Game[]>([]);
  const [guildRoster, setGuildRoster] = useState<CreateRaidCharacter[]>([]);
  const [guildId, setGuildId] = useState(guilds[0]?.id ?? "");
  const selectedGuild = useMemo(() => guilds.find((g) => g.id === guildId) ?? guilds[0] ?? null, [guildId, guilds]);

  // Games this guild plays (main + added, e.g. WoW Forever). The raid is created
  // for ONE of them: its instances, roster and the raid row all use that game.
  const [guildGames, setGuildGames] = useState<{ gameId: string; gameName: string }[]>([]);
  const [raidGameId, setRaidGameId] = useState<string>("");
  useEffect(() => {
    setGuildGames([]);
    setRaidGameId("");
    if (!selectedGuild) return;
    fetch(`/guilds/${selectedGuild.id}/games`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        // Active games only: a retired game takes no new raids.
        const list = (d.games || [])
          .filter((g: any) => g.isActive !== false)
          .map((g: any) => ({ gameId: g.gameId, gameName: String(g.gameName || "").replace(/^World of Warcraft:?\s*/i, "") || g.gameName }));
        setGuildGames(list);
      })
      .catch(() => {});
  }, [selectedGuild]);
  // Chosen game, else the one the guild row points at if still active, else the first active one.
  const activeGameId =
    raidGameId ||
    (guildGames.some((g) => g.gameId === selectedGuild?.gameId) ? selectedGuild?.gameId : guildGames[0]?.gameId) ||
    selectedGuild?.gameId || "";

  const [raidLabel, setRaidLabel] = useState("");
  const [raidOptionId, setRaidOptionId] = useState<number | null>(null);
  const [raidOptions, setRaidOptions] = useState<FieldOption[]>([]);
  const [currentGameFields, setCurrentGameFields] = useState<FieldGroup[] | null>(null);
  // The guild's reset rule. Every week this page picks (roster pool, lockouts,
  // suggestions, release) is this guild's, never the EU default: for a US guild
  // that default is a day off every Tuesday.
  const guildRule = useMemo(
    () => ruleForRegionName(selectedGuild?.region, currentGameFields as any),
    [selectedGuild?.region, currentGameFields]
  );

  // --- SESSION CONFIG ---
  // UI Modes: "single" | "multi" | "series"
  const [scheduleMode, setScheduleMode] = useState<"single" | "multi" | "series">("single");
  
  const [sessionList, setSessionList] = useState<SingleSessionConfig[]>(() => [
    {
      date: DateTime.now().setZone(userTz).toFormat("yyyy-MM-dd"),
      startTime: "20:00",
      endTime: "23:00",
    },
  ]);
  
  // For Single Mode, we just use the first entry of sessionList for state ease
  // For Series, we use recurringPatterns
  const [recurringPatterns, setRecurringPatterns] = useState<Record<string, DayPattern>>({});

  const [size, setSize] = useState<RaidSize>(10);
  const [raidLeaderId, setRaidLeaderId] = useState<string>("");
  const [lootMasterId, setLootMasterId] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [softReserveEnabled, setSoftReserveEnabled] = useState(false);
  const [maxSoftReserves, setMaxSoftReserves] = useState(2);
  
  // Raid chaining state: additional raid_option_ids linked to this raid
  const [chainedRaidOptionIds, setChainedRaidOptionIds] = useState<number[]>([]);

  const [rosterSlots, setRosterSlots] = useState<(string | null)[]>([]);
  const [rosterRoles, setRosterRoles] = useState<(string | null)[]>([]);
  // True bench: backup players, unbounded, NOT truncated by raid size.
  const [benchSlots, setBenchSlots] = useState<string[]>([]);
  const [benchRoles, setBenchRoles] = useState<(string | null)[]>([]);

  // Drag State
  const [dragOrigin, setDragOrigin] = useState<DragOrigin>(null);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredCharacterId, setHoveredCharacterId] = useState<string | null>(null);
  const [hoveredOwnerKey, setHoveredOwnerKey] = useState<string | null>(null);
  
  // Squad state
  const [squadLookup, setSquadLookup] = useState<SquadLookup>({});
  const [compositionTags, setCompositionTags] = useState<CompositionTag[]>([]);
  const [buffPanelOpen, setBuffPanelOpen] = useState(false);
  const [hoveredSpecTags, setHoveredSpecTags] = useState<Set<string>>(new Set());
  const [hoveredSquadId, setHoveredSquadId] = useState<string | null>(null);
  
  // Squad intercept modal state
  const [squadIntercept, setSquadIntercept] = useState<{
    triggerCharId: string;
    triggerCharName: string;
    triggerCharClassName: string;
    triggerCharRoles: string[];
    triggerCharUserId: string;
    squadInfo: SquadInfo;
    targetIndex: number;
    availableSquadmates: { id: string; name: string; className: string; roles: string[]; userId: string }[];
  } | null>(null);
  
  // Squad selection state: { userId: { charId, roleLabel } }
  const [squadSelections, setSquadSelections] = useState<Record<string, { charId: string; roleLabel: string }>>({});
  
  // User schedules for client-side busy calculation
  const [userSchedules, setUserSchedules] = useState<Record<string, { startsAt: string; durationMinutes: number }[]>>({});
  const [schedulesLoaded, setSchedulesLoaded] = useState(false);
  // 0 = this reset, 1 = next reset. Default depends on how far through the current Wed→Tue week
  // we are: once a slim majority is in the past, "next reset" is the more useful planning target,
  // since past slots are filtered out of the suggestion grid (see scoring loop below).
  const [suggestionsWeekOffset, setSuggestionsWeekOffset] = useState(() => {
    // Hours since this guild's reset instant (the label at midnight is wrong
    // for every region). Fields may not be loaded yet, then it is EU.
    const weekStartInstant = resetInstantForWeek(getCurrentResetWeek(guildRule), guildRule);
    const hoursIntoWeek = DateTime.utc().diff(weekStartInstant, 'hours').hours;
    return hoursIntoWeek >= 24 * 4 ? 1 : 0; // flip to "next" once we're 4+ days into the current week
  });
  // Whether user has acknowledged they want to split the squad
  const [allowSquadSplit, setAllowSquadSplit] = useState(false);
  const rosterFetchIdRef = useRef(0);
  // --- EFFECTS ---
  useEffect(() => {
    setRosterSlots((prev: any[]) => {
      const next = prev.slice(0, size);
      while (next.length < size) next.push(null);
      return next;
    });
    setRosterRoles((prev: any[]) => {
      const next = prev.slice(0, size);
      while (next.length < size) next.push(null);
      return next;
    });
  }, [size]);

  useEffect(() => {
    fetch("/games", { credentials: "include" }).then((res) => (res.ok ? res.json() : [])).then((data) => setGames(Array.isArray(data) ? data : [])).catch(() => setGames([]));
  }, []);

  useEffect(() => {
    if (!selectedGuild) { setGuildRoster([]); setSchedulesLoaded(false); return; }
    if (!raidOptionId) { setSchedulesLoaded(false); return; }
    setSchedulesLoaded(false); // Reset on new fetch
    
    // Calculate the target week based on selected sessions (if any) or default to next reset
    let weekStart: string | undefined;
    if (sessionList.length > 0 && sessionList[0].date) {
      const anchorTime = sessionList[0].startTime || "12:00";
      const sessionInstant = new Date(pickerPartsToUtc(sessionList[0].date, anchorTime, userTz));
      weekStart = getResetWeekForDate(sessionInstant, guildRule);
    }
    
    // On Step 0 (suggestions panel), use the week offset toggle to control which week's data we fetch.
    // On Step 1+ the session date (computed above) is the source of truth.
    if (step === 0 || !weekStart) {
      weekStart =
        DateTime.fromISO(getCurrentResetWeek(guildRule), { zone: "utc" })
          .plus({ days: suggestionsWeekOffset * 7 })
          .toISODate() || getCurrentResetWeek(guildRule);
    }
    
    let url = `/guilds/${selectedGuild.id}`;
    const params = new URLSearchParams();
    if (weekStart) params.append('weekStart', weekStart);
    // Pass all raid option IDs (primary + chained) for chain lockout checking
    const allOptionIds = [raidOptionId, ...chainedRaidOptionIds].filter(Boolean);
    if (allOptionIds.length > 1) {
      params.append('raidOptionIds', allOptionIds.join(','));
    } else if (raidOptionId) {
      params.append('raidOptionId', String(raidOptionId));
    }
    
    // Build session windows for bilocation check (format: start1,end1|start2,end2)
    // Only send sessions on Step 1+ — on Step 0 the suggestions panel handles busy checks client-side,
    // and sending a stale sessionList date causes the backend to compute lockouts for the wrong week
    const activeSessions = step > 0 ? (scheduleMode === "single" ? [sessionList[0]] : sessionList) : [];
    const sessionWindowsStr = activeSessions
      .filter((s: SingleSessionConfig) => s.date && s.startTime && s.endTime)
      .map((s: SingleSessionConfig) => {
        const start = new Date(pickerPartsToUtc(s.date, s.startTime, userTz));
        let end = new Date(pickerPartsToUtc(s.date, s.endTime, userTz));
        if (end.getTime() <= start.getTime()) end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
        return `${start.toISOString()},${end.toISOString()}`;
      })
      .join('|');
    if (sessionWindowsStr) {
      params.append('sessions', encodeURIComponent(sessionWindowsStr));
    }
    
    if (params.toString()) url += '?' + params.toString();
    
    const fetchId = ++rosterFetchIdRef.current;
    fetch(url, { credentials: "include" }).then((res) => (res.ok ? res.json() : null)).then((data) => {
        if (fetchId !== rosterFetchIdRef.current) return;
        if (!data || !Array.isArray(data.roster)) return;
        const mapped = data.roster
          .filter((row: any) => !row.gameId || !activeGameId || row.gameId === activeGameId)
          .map((row: any) => {
          const meta = row.metadata || row.character_metadata || {};
          return {
            id: String(row.characterId ?? row.character_id),
            name: String(row.name ?? row.character_name ?? ""),
            className: meta.class_name || meta.className,
            primaryRole: meta.primary_role || meta.primaryRole,
            guildId: String(selectedGuild.id),
            metadata: meta,
            options: row.options,
            userId: row.userId ?? row.user_id,
            ownerDiscordId: row.userDiscordId ?? row.user_discord_id,
            ownerDisplayName: row.userDisplayName ?? row.user_display_name,
            ownerAvailabilitySlots: typeof row.userAvailability === "string" ? row.userAvailability.split("").map((b: string) => b === "1") : undefined,
            isConfirmed: row.isConfirmed !== false, // Default true for legacy
            isLockedOut: row.isLockedOut === true,
            isBusy: row.isBusy === true,
          };
        });
        setGuildRoster(mapped);
        
        // Store user schedules for client-side busy calculation in suggestions
        setUserSchedules(data.userSchedules || {});
        setSchedulesLoaded(true);
        
        // Fetch squad info for all users in roster
        const uniqueUserIds = Array.from(new Set(mapped.map((c: any) => c.userId).filter(Boolean)));
        if (uniqueUserIds.length > 0) {
          fetch(`/api/squads/by-users?userIds=${uniqueUserIds.join(',')}`, { credentials: "include" })
            .then(r => r.ok ? r.json() : { squads: {} })
            .then(data => setSquadLookup(data.squads || {}))
            .catch(() => setSquadLookup({}));
        }
      }).catch((e) => console.error(e));
    }, [selectedGuild, step > 0 ? sessionList : null, raidOptionId, scheduleMode, chainedRaidOptionIds, suggestionsWeekOffset, step, userTz, activeGameId, guildRule]);

  useEffect(() => {
    if (!selectedGuild || !activeGameId) { setCurrentGameFields(null); setRaidOptions([]); return; }
    fetch(`/games/${encodeURIComponent(activeGameId)}/fields`, { credentials: "include" }).then((res) => (res.ok ? res.json() : [])).then((data: FieldGroup[]) => {
        setCurrentGameFields(data);
        const raidGroup = data.find((g) => g.key === "raid_interest" || g.key === "raid_preferences");
        const opts = raidGroup?.options ?? [];
        setRaidOptions(opts);
        if (!raidOptionId && opts.length > 0) {
          setRaidOptionId(opts[0].id);
          if (!raidLabel) setRaidLabel(opts[0].label);
          if (opts[0].metadata?.size) setSize(opts[0].metadata.size as RaidSize);
        }
      }).catch(() => {});
      fetch(`/games/${encodeURIComponent(activeGameId)}/composition-tags`, { credentials: "include" })
        .then((res) => (res.ok ? res.json() : []))
        .then((data: CompositionTag[]) => setCompositionTags(data))
        .catch(() => setCompositionTags([]));
      }, [selectedGuild, sessionList, activeGameId]);

  // Clear raid leader / master looter  if they're removed from roster
  useEffect(() => {
    if (raidLeaderId && !rosterSlots.includes(raidLeaderId)) {
      setRaidLeaderId("");
    }
    if (lootMasterId && !rosterSlots.includes(lootMasterId)) {
      setLootMasterId("");
    }
  }, [rosterSlots, raidLeaderId, lootMasterId]);

  useEffect(() => {
    if (!dragOrigin) return;
    function handleMouseMove(e: MouseEvent) { setIsDragging(true); setDragPosition({ x: e.clientX, y: e.clientY }); }
    function handleMouseUp(e: MouseEvent) {
      let targetSlotIndex: number | null = null;
      const x = e.clientX;
      const y = e.clientY;
      slotRefs.current.forEach((el: any, idx: number) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) { if (targetSlotIndex === null) targetSlotIndex = idx; }
      });
      // Is the cursor over the true-bench drop zone? (A seat always wins as a promotion target.)
      let overBench = false;
      if (benchZoneRef.current) {
        const br = benchZoneRef.current.getBoundingClientRect();
        if (x >= br.left && x <= br.right && y >= br.top && y <= br.bottom) overBench = true;
      }
      if (targetSlotIndex === null && overBench) {
        if (dragOrigin?.type === "bench") {
          // Available pool -> bench (single char; no squad auto-pull on the bench).
          const { charId, roleLabel } = dragOrigin;
          if (!benchSlots.includes(charId)) {
            setBenchSlots((prev) => [...prev, charId]);
            setBenchRoles((prev) => [...prev, roleLabel || null]);
          }
        } else if (dragOrigin?.type === "slot") {
          // Demote a seated player to the bench (single char only).
          const idx = dragOrigin.slotIndex;
          const charId = dragOrigin.charId;
          const role = rosterRoles[idx] || null;
          setRosterSlots((prev: any[]) => { const next = [...prev]; next[idx] = null; return next; });
          setRosterRoles((prev: any[]) => { const next = [...prev]; next[idx] = null; return next; });
          if (charId && !benchSlots.includes(charId)) {
            setBenchSlots((prev) => [...prev, charId]);
            setBenchRoles((prev) => [...prev, role]);
          }
        }
        // "benchseat" dropped back on the bench = no-op.
        setDragOrigin(null); setDragPosition(null); setIsDragging(false);
        return;
      }
      if (targetSlotIndex !== null) {
        if (dragOrigin?.type === "slot") {
          const fromIdx = dragOrigin.slotIndex;
          const toIdx = targetSlotIndex;
          setRosterSlots((prev: any[]) => { const next = [...prev]; const tmp = next[toIdx]; next[toIdx] = next[fromIdx]; next[fromIdx] = tmp; return next; });
          setRosterRoles((prev: any[]) => { const next = [...prev]; const tmp = next[toIdx]; next[toIdx] = next[fromIdx]; next[fromIdx] = tmp; return next; });
        } else if (dragOrigin?.type === "bench") {
          const { charId, roleLabel } = dragOrigin;
          
          // Check if this character is in a squad
          const charData = charById.get(charId);
          const charUserId = charData?.userId || (charData as any)?.ownerId;
          const charSquadInfo = charUserId ? squadLookup[charUserId] : null;
          
          if (charSquadInfo && charSquadInfo.squadSize > 1) {
            // Find other squad members who are available on the bench
            const seatedCharIds = new Set(rosterSlots.filter(Boolean));
            const seatedUserIds = new Set<string>();
            rosterSlots.forEach((slotId, idx) => {
              if (!slotId) return;
              const c = charById.get(slotId);
              const uid = c?.userId || (c as any)?.ownerId;
              if (uid) seatedUserIds.add(uid);
            });
            
            // Get ALL characters for each squad member (including alts)
            const squadMemberChars: { id: string; name: string; className: string; roles: string[]; userId: string }[] = [];
            benchAvailable.forEach((c: any) => {
              if (c.id === charId) return; // Exclude the dragged character
              const uid = c.userId || c.ownerId;
              if (!uid || !charSquadInfo.memberUserIds.includes(uid)) return; // Not in squad
              if (seatedCharIds.has(c.id)) return; // Already seated
              if (seatedUserIds.has(uid)) return; // User already has a char seated
              squadMemberChars.push({
                id: c.id,
                name: c.name,
                className: c.metadata?.class_name || c.className || "Unknown",
                roles: resolveRoleLabels(c, currentGameFields),
                userId: uid
              });
            });
            
            // Also include trigger user's other characters (alts)
            const triggerUserAlts = benchAvailable.filter((c: any) => {
              if (c.id === charId) return false;
              const uid = c.userId || c.ownerId;
              return uid === charUserId && !seatedCharIds.has(c.id);
            }).map((c: any) => ({
              id: c.id,
              name: c.name,
              className: c.metadata?.class_name || c.className || "Unknown",
              roles: resolveRoleLabels(c, currentGameFields),
              userId: c.userId || c.ownerId
            }));
            
            const availableSquadmates = squadMemberChars;
            
            // If there are available squadmates, show intercept modal
            if (availableSquadmates.length > 0) {
              const triggerRoles = resolveRoleLabels(charData, currentGameFields);
              setSquadIntercept({
                triggerCharId: charId,
                triggerCharName: charData?.name || "Unknown",
                triggerCharClassName: charData?.metadata?.class_name || "Unknown",
                triggerCharRoles: triggerRoles,
                triggerCharUserId: charUserId,
                squadInfo: charSquadInfo,
                targetIndex: targetSlotIndex!,
                availableSquadmates
              });
              // Initialize selections with ALL squad members (trigger + squadmates)
              const initialSelections: Record<string, { charId: string; roleLabel: string }> = {
                [charUserId]: { charId: charId, roleLabel: roleLabel || triggerRoles[0] || "DPS" }
              };
              availableSquadmates.forEach(sm => {
                if (!initialSelections[sm.userId]) {
                  initialSelections[sm.userId] = { charId: sm.id, roleLabel: sm.roles[0] || "DPS" };
                }
              });
              setSquadSelections(initialSelections);
              setAllowSquadSplit(false); // Reset split mode
              setDragOrigin(null); setDragPosition(null); setIsDragging(false);
              return; // Don't proceed with normal drop - wait for modal
            }
          }
          
          // Normal drop (no squad or no available squadmates)
          setRosterSlots((prev: any[]) => { const next = [...prev]; next[targetSlotIndex!] = charId; return next; });
          if (roleLabel) { setRosterRoles((prev: any[]) => { const next = [...prev]; next[targetSlotIndex!] = roleLabel; return next; }); }
        } else if (dragOrigin?.type === "benchseat") {
          // Promote bench -> seat. Leaves the bench; gains a seat_index on save (auto-corrects).
          // Any player already in the target seat is returned to the available pool.
          const { charId, roleLabel } = dragOrigin;
          const bIdx = benchSlots.indexOf(charId);
          setBenchSlots((prev) => prev.filter((id) => id !== charId));
          setBenchRoles((prev) => prev.filter((_, i) => i !== bIdx));
          setRosterSlots((prev: any[]) => { const next = [...prev]; next[targetSlotIndex!] = charId; return next; });
          setRosterRoles((prev: any[]) => { const next = [...prev]; next[targetSlotIndex!] = roleLabel || null; return next; });
        }
      } else {
        if (dragOrigin?.type === "slot") {
          const idx = dragOrigin.slotIndex;
          const draggedCharId = dragOrigin.charId;
          const draggedChar = charById.get(draggedCharId);
          const draggedUserId = draggedChar ? ((draggedChar as any).userId || (draggedChar as any).ownerId) : null;
          const draggedSquadInfo = draggedUserId ? squadLookup[draggedUserId] : null;
          
          // If squad member, remove all squad mates (same as X button)
          if (draggedSquadInfo && draggedSquadInfo.squadSize > 1) {
            const squadUserIds = new Set(draggedSquadInfo.memberUserIds);
            const indicesToClear: number[] = [];
            
            rosterSlots.forEach((id, slotIdx) => {
              if (!id) return;
              const c = charById.get(id);
              const uid = c ? ((c as any).userId || (c as any).ownerId) : null;
              if (uid && squadUserIds.has(uid)) {
                indicesToClear.push(slotIdx);
              }
            });
            
            setRosterSlots((prev: any[]) => {
              const next = [...prev];
              indicesToClear.forEach(i => { next[i] = null; });
              return next;
            });
            setRosterRoles((prev: any[]) => {
              const next = [...prev];
              indicesToClear.forEach(i => { next[i] = null; });
              return next;
            });
          } else {
            // Just remove this slot
            setRosterSlots((prev: any[]) => { const next = [...prev]; next[idx] = null; return next; });
            setRosterRoles((prev: any[]) => { const next = [...prev]; next[idx] = null; return next; });
          }
        } else if (dragOrigin?.type === "benchseat") {
          // Bench card dropped outside any zone -> return to the available pool.
          const charId = dragOrigin.charId;
          const bIdx = benchSlots.indexOf(charId);
          setBenchSlots((prev) => prev.filter((id) => id !== charId));
          setBenchRoles((prev) => prev.filter((_, i) => i !== bIdx));
        }
      }
      setDragOrigin(null); setDragPosition(null); setIsDragging(false);
    }
    window.addEventListener("mousemove", handleMouseMove); window.addEventListener("mouseup", handleMouseUp);
    return () => { window.removeEventListener("mousemove", handleMouseMove); window.removeEventListener("mouseup", handleMouseUp); };
  }, [dragOrigin]);

  // Handle seating just the single character from squad intercept
  const handleSeatSingle = () => {
    if (!squadIntercept) return;
    const { triggerCharId, triggerCharRoles, targetIndex } = squadIntercept;
    const roleLabel = triggerCharRoles[0] || "DPS";
    
    setRosterSlots((prev: any[]) => { const next = [...prev]; next[targetIndex] = triggerCharId; return next; });
    setRosterRoles((prev: any[]) => { const next = [...prev]; next[targetIndex] = roleLabel; return next; });
    setSquadIntercept(null);
    setSquadSelections({});
    setAllowSquadSplit(false);
  };

  // Handle seating entire squad from intercept
  const handleSeatSquad = () => {
    if (!squadIntercept) return;
    const { targetIndex } = squadIntercept;
    
    // Get all selected characters
    const selections = Object.values(squadSelections).filter(s => s.charId);
    if (selections.length === 0) return;
    
    // Find empty slots, excluding targetIndex (trigger char will use that)
    const emptySlots: number[] = [];
    for (let i = 0; i < size; i++) {
      if (!rosterSlots[i] && i !== targetIndex) {
        emptySlots.push(i);
      }
    }
    
    // Seat each character
    const newSlots = [...rosterSlots];
    const newRoles = [...rosterRoles];
    let emptyIdx = 0;
    
    selections.forEach((sel, idx) => {
      // First character goes to targetIndex, rest to empty slots
      const seatIndex = idx === 0 ? targetIndex : emptySlots[emptyIdx++];
      if (seatIndex === undefined || seatIndex >= size) return; // No more slots
      
      newSlots[seatIndex] = sel.charId;
      newRoles[seatIndex] = sel.roleLabel;
    });
    
    setRosterSlots(newSlots);
    setRosterRoles(newRoles);
    setSquadIntercept(null);
    setSquadSelections({});
    setAllowSquadSplit(false);
  };

  // --- COMPUTED DATA ---
  const availableGuildCharacters = guildRoster.length > 0 ? guildRoster : guilds.length > 0 && selectedGuild ? characters.filter((c:any) => c.guildId === selectedGuild.id) : [];
  const charById = useMemo(() => { const map = new Map<string, CreateRaidCharacter>(); for (const c of availableGuildCharacters) map.set(c.id, c); return map; }, [availableGuildCharacters]);
  const assignedIds = new Set([...rosterSlots.filter((id): id is string => !!id), ...benchSlots]);
  const buffCoverage = useMemo(() => computeBuffCoverage(rosterSlots, rosterRoles, charById, currentGameFields, compositionTags), [rosterSlots, rosterRoles, charById, currentGameFields, compositionTags]);
  const assignedOwnerKeys = useMemo(() => { const owners = new Set<string>(); for (const slotId of [...rosterSlots, ...benchSlots]) { if (!slotId) continue; const char = charById.get(String(slotId)); if (!char) continue; const ownerKey = getOwnerKeyForChar(char, me); if (ownerKey) owners.add(ownerKey); } return owners; }, [rosterSlots, benchSlots, charById, me]);

  const calculatedWindows = useMemo(() => {
      let lockoutWeekIso: string;
      if (sessionList.length > 0 && sessionList[0]?.date) {
        const anchorTime = sessionList[0].startTime || "12:00";
        lockoutWeekIso = getResetWeekForDate(new Date(pickerPartsToUtc(sessionList[0].date, anchorTime, userTz)), guildRule);
      } else {
        lockoutWeekIso =
          DateTime.fromISO(getCurrentResetWeek(guildRule), { zone: "utc" })
            .plus({ days: (step === 0 ? suggestionsWeekOffset : 0) * 7 })
            .toISODate() || getCurrentResetWeek(guildRule);
      }

      if (scheduleMode === "series") {
          return getRecurringRaidWindows(recurringPatterns, lockoutWeekIso, userTz);
      }
      const activeSessions = scheduleMode === "single" ? [sessionList[0]] : sessionList;
      const formatted = activeSessions.map((s: any) => {
          const start = new Date(pickerPartsToUtc(s.date, s.startTime, userTz));
          let end = new Date(pickerPartsToUtc(s.date, s.endTime, userTz));
          if (end.getTime() <= start.getTime()) end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
          const durationMinutes = Math.round((end.getTime() - start.getTime()) / 60000);
          return { startsAt: start, durationMinutes };
      });
      return getSessionWindows(formatted, lockoutWeekIso, userTz);
  }, [scheduleMode, recurringPatterns, sessionList, userTz, step, suggestionsWeekOffset, guildRule]);

  function checkAvailability(c: CreateRaidCharacter) {
    if ((c as any).isConfirmed === false) return false;
    if (!calculatedWindows || calculatedWindows.length === 0) return true;
    const slots = (c as any).ownerAvailabilitySlots;
    if (!Array.isArray(slots) || slots.length !== TOTAL_AVAIL_SLOTS) return false;
    return isCharacterAvailable({ ...c, availabilityBits: slots.map((b: any)=>b?'1':'0').join('') } as any, calculatedWindows);
  }

  // Characters in roster who are willing to raid lead
  const eligibleRaidLeaders = useMemo(() => 
    availableGuildCharacters.filter((c: any) => 
      rosterSlots.includes(c.id) && isWillingRaidLead(c, currentGameFields)
    ), [availableGuildCharacters, rosterSlots, currentGameFields]);

  // Characters in roster who are willing to be master looter
  const eligibleLootMasters = useMemo(() => 
    availableGuildCharacters.filter((c: any) => 
      rosterSlots.includes(c.id) && isWillingLootMaster(c, currentGameFields)
    ), [availableGuildCharacters, rosterSlots, currentGameFields]);

    const benchAvailable = useMemo(() => {
      const raidInterestGroup = currentGameFields?.find((g: any) => g.key === 'raid_interest' || g.key === 'raid_preferences');
      
      // Build a set of user IDs who have expressed ANY raid interest on ANY character
      const usersWithAnyRaidInterest = new Set<string>();
      if (raidInterestGroup) {
        availableGuildCharacters.forEach((c: any) => {
          const ownerId = c.userId || c.user_id || c.ownerId;
          if (!ownerId) return;
          const hasAnyInterest = Array.isArray(c.options) && c.options.some((opt: any) => Number(opt.group_id) === Number(raidInterestGroup.id));
          if (hasAnyInterest) usersWithAnyRaidInterest.add(ownerId);
        });
      }
      
      return availableGuildCharacters.filter((c: any) => { 
        if (assignedIds.has(c.id)) return false; 
        if (!checkAvailability(c)) return false; 
        const ownerKey = getOwnerKeyForChar(c, me); 
        if (ownerKey && assignedOwnerKeys.has(ownerKey)) return false; 
        
        // Character-level lockout: already assigned to same raid instance this week
        if (c.isLockedOut === true) return false;
        // User-level bilocation: user has another character in a raid at this time
        if (c.isBusy === true) return false;        
       
        if (raidOptionId && raidInterestGroup) {
          const ownerId = c.userId || c.user_id || c.ownerId;
          const userHasExpressedPreferences = ownerId && usersWithAnyRaidInterest.has(ownerId);
          const raidInterestOptions = Array.isArray(c.options) ? c.options.filter((opt: any) => Number(opt.group_id) === Number(raidInterestGroup.id)) : [];
          
          // Must have interest in ALL raids in the chain (primary + chained)
          const allRequiredOptionIds = [raidOptionId, ...chainedRaidOptionIds];
          const hasInterestInAllRaids = allRequiredOptionIds.every(optId => 
            raidInterestOptions.some((opt: any) => Number(opt.option_id) === Number(optId))
          );
          
          // If user has expressed preferences anywhere, character must have ALL chain raids selected
          if (userHasExpressedPreferences && !hasInterestInAllRaids) return false;
        }
        return true; 
      });
    }, [availableGuildCharacters, assignedIds, calculatedWindows, assignedOwnerKeys, me, raidOptionId, currentGameFields]);
  const lockedByOwnerCharacters = useMemo(() => availableGuildCharacters.filter((c: any) => { if (assignedIds.has(c.id)) return false; const ownerKey = getOwnerKeyForChar(c, me); if (!ownerKey) return false; return assignedOwnerKeys.has(ownerKey); }), [availableGuildCharacters, assignedIds, assignedOwnerKeys, me]);
  const benchUnavailable = useMemo(() => availableGuildCharacters.filter((c: any) => !assignedIds.has(c.id) && !checkAvailability(c) && c.isLockedOut !== true && c.isBusy !== true), [availableGuildCharacters, assignedIds, calculatedWindows]);
  
  const benchLockedOut = useMemo(() => availableGuildCharacters.filter((c: any) => {
    if (assignedIds.has(c.id)) return false;
    const ownerKey = getOwnerKeyForChar(c, me);
    if (ownerKey && assignedOwnerKeys.has(ownerKey)) return false;
    return c.isLockedOut === true && c.isBusy !== true;
  }), [availableGuildCharacters, assignedIds, assignedOwnerKeys, me]);
  
  // Characters whose user has another char scheduled at this time (bilocation)
  const benchBusy = useMemo(() => availableGuildCharacters.filter((c: any) => {
    if (assignedIds.has(c.id)) return false;
    const ownerKey = getOwnerKeyForChar(c, me);
    if (ownerKey && assignedOwnerKeys.has(ownerKey)) return false;
    return c.isBusy === true;
  }), [availableGuildCharacters, assignedIds, assignedOwnerKeys, me]);
  
  const benchNotInterested = useMemo(() => {
    const raidInterestGroup = currentGameFields?.find((g: any) => g.key === 'raid_interest' || g.key === 'raid_preferences');
    
    // Build a set of user IDs who have expressed ANY raid interest on ANY character
    const usersWithAnyRaidInterest = new Set<string>();
    if (raidInterestGroup) {
      availableGuildCharacters.forEach((c: any) => {
        const ownerId = c.userId || c.user_id || c.ownerId;
        if (!ownerId) return;
        const hasAnyInterest = Array.isArray(c.options) && c.options.some((opt: any) => Number(opt.group_id) === Number(raidInterestGroup.id));
        if (hasAnyInterest) usersWithAnyRaidInterest.add(ownerId);
      });
    }
    
    return availableGuildCharacters.filter((c: any) => {
      if (assignedIds.has(c.id)) return false;
      if (!checkAvailability(c)) return false;
      const ownerKey = getOwnerKeyForChar(c, me);
      if (ownerKey && assignedOwnerKeys.has(ownerKey)) return false;
      // Exclude chars that should be in other sections
      if (c.isBusy === true) return false;
      if (c.isLockedOut === true) return false;
      
      if (raidOptionId && raidInterestGroup) {
        const ownerId = c.userId || c.user_id || c.ownerId;
        const userHasExpressedPreferences = ownerId && usersWithAnyRaidInterest.has(ownerId);
        const raidInterestOptions = Array.isArray(c.options) ? c.options.filter((opt: any) => Number(opt.group_id) === Number(raidInterestGroup.id)) : [];
        
        // Chain-aware: check if character has interest in ALL raids in chain
        const allRequiredOptionIds = [raidOptionId, ...chainedRaidOptionIds];
        const hasInterestInAllRaids = allRequiredOptionIds.every(optId => 
          raidInterestOptions.some((opt: any) => Number(opt.option_id) === Number(optId))
        );
        
        // Show here if user has expressed preferences AND character doesn't have ALL chain raids
        return userHasExpressedPreferences && !hasInterestInAllRaids;
      }
      return false;
    });
  }, [availableGuildCharacters, assignedIds, calculatedWindows, assignedOwnerKeys, me, raidOptionId, currentGameFields]);

  const groupedBench = useMemo(() => { const groups: Record<string, CreateRaidCharacter[]> = {}; benchAvailable.forEach((c: any) => { const roles = resolveRoleLabels(c, currentGameFields); const effectiveRoles = roles.length > 0 ? roles : ["Unspecified"]; effectiveRoles.forEach(role => { if (!groups[role]) groups[role] = []; groups[role].push(c); }); }); return groups; }, [benchAvailable, currentGameFields]);
  const sortedRoles = Object.keys(groupedBench).sort((a, b) => { const ia = ROLE_ORDER.indexOf(a); const ib = ROLE_ORDER.indexOf(b); return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib); });

  // --- PLAYER-CENTRIC BENCH: Group characters by userId into player cards ---
  const [benchRoleFilter, setBenchRoleFilter] = useState<string | null>(null);

  const playerBenchCards: PlayerBenchEntry[] = useMemo(() => {
    const playerMap = new Map<string, PlayerBenchEntry>();
    const playerCharOrder = new Map<string, string[]>();
    
    benchAvailable.forEach((c: any) => {
      const userId = c.userId || c.user_id || c.ownerId;
      if (!userId) return;
      
      const specEntries = resolveSpecEntries(c, currentGameFields);
      const className = c.className || c.metadata?.class_name || "Unknown";
      const displayName = c.ownerDisplayName || c.owner_display_name || c.name || "Unknown";
      const ownerKey = getOwnerKeyForChar(c, me);
      const charSquadInfo = squadLookup[userId] || null;
      
      if (!playerMap.has(userId)) {
        playerMap.set(userId, {
          userId,
          displayName,
          primaryCharName: c.name,
          charCount: 0,
          specs: [],
          squadInfo: charSquadInfo,
          ownerKey,
        });
        playerCharOrder.set(userId, []);
      }
      
      const player = playerMap.get(userId)!;
      const charOrder = playerCharOrder.get(userId)!;
      
      if (!charOrder.includes(c.id)) {
        charOrder.push(c.id);
        player.charCount = charOrder.length;
        if (charOrder.length === 1) player.primaryCharName = c.name;
      }
      
      specEntries.forEach(entry => {
        const exists = player.specs.some(s => s.charId === c.id && s.specLabel === entry.specLabel);
        if (!exists) {
          player.specs.push({
            charId: c.id,
            charName: c.name,
            className,
            specLabel: entry.specLabel,
            roleLabel: entry.roleLabel,
            roleColor: getRoleColorForRoleLabel(c, currentGameFields, entry.roleLabel) || getPrimaryRoleColorForChar(c, currentGameFields),
            char: c,
          });
        }
      });
    });
    
    playerMap.forEach(player => {
      player.specs.sort((a, b) => {
        const ia = ROLE_ORDER.indexOf(a.roleLabel);
        const ib = ROLE_ORDER.indexOf(b.roleLabel);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });
    });
    
    return Array.from(playerMap.values()).sort((a, b) => {
      if (b.specs.length !== a.specs.length) return b.specs.length - a.specs.length;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [benchAvailable, currentGameFields, squadLookup, me]);

  /*
   * Filter players by role, and resolve how many specs each card will actually
   * SHOW — under a role filter that differs from the player's total.
   *
   * The visible count drives both the sort and the card width: narrow cards
   * (<= BENCH_PAIR_MAX_SPECS) pair up two-per-row, so ordering by visible count
   * descending keeps them contiguous. Without that, a wide card landing between
   * two narrow ones would strand each of them on its own row.
   */
  const filteredPlayerBench = useMemo(() => {
    const rows = playerBenchCards.map((player) => ({
      player,
      visibleSpecs: benchRoleFilter ? player.specs.filter((s) => s.roleLabel === benchRoleFilter) : player.specs,
    }));
    const shown = benchRoleFilter ? rows.filter((r) => r.visibleSpecs.length > 0) : rows;
    return shown.sort((a, b) => {
      if (b.visibleSpecs.length !== a.visibleSpecs.length) return b.visibleSpecs.length - a.visibleSpecs.length;
      return a.player.displayName.localeCompare(b.player.displayName);
    });
  }, [playerBenchCards, benchRoleFilter]);

  /*
   * Split the pool into at most two columns.
   *
   * Row count can't be read off the card count, because narrow cards pair up —
   * so walk the ordered list and simulate the packing first. Once that exceeds
   * BENCH_MAX_ROWS_PER_COLUMN the list splits in half by ROW, not by card, which
   * keeps the two columns level regardless of how the pairing fell.
   *
   * Beyond two columns the pool just gets taller and scrolls, as it always has.
   * The split is computed unconditionally and the CSS decides whether the two
   * columns sit side by side or stack — below 1800px they stack, which restores
   * exactly the original single-column order.
   */
  const benchColumns = useMemo(
    () => splitIntoBenchColumns(filteredPlayerBench, (entry) => entry.visibleSpecs.length),
    [filteredPlayerBench]
  );

  // Build disabled player cards for other sections too
  function buildDisabledPlayerCards(chars: CreateRaidCharacter[]): PlayerBenchEntry[] {
    const playerMap = new Map<string, PlayerBenchEntry>();
    const playerCharOrder = new Map<string, string[]>();
    chars.forEach((c: any) => {
      const userId = c.userId || c.user_id || c.ownerId;
      if (!userId) return;
      const specEntries = resolveSpecEntries(c, currentGameFields);
      const className = c.className || c.metadata?.class_name || "Unknown";
      const displayName = c.ownerDisplayName || c.owner_display_name || c.name || "Unknown";
      const ownerKey = getOwnerKeyForChar(c, me);
      const charSquadInfo = squadLookup[userId] || null;
      if (!playerMap.has(userId)) {
        playerMap.set(userId, { userId, displayName, primaryCharName: c.name, charCount: 0, specs: [], squadInfo: charSquadInfo, ownerKey });
        playerCharOrder.set(userId, []);
      }
      const player = playerMap.get(userId)!;
      const charOrder = playerCharOrder.get(userId)!;
      if (!charOrder.includes(c.id)) { charOrder.push(c.id); player.charCount = charOrder.length; if (charOrder.length === 1) player.primaryCharName = c.name; }
      specEntries.forEach(entry => {
        if (!player.specs.some(s => s.charId === c.id && s.specLabel === entry.specLabel)) {
          player.specs.push({ charId: c.id, charName: c.name, className, specLabel: entry.specLabel, roleLabel: entry.roleLabel, roleColor: getRoleColorForRoleLabel(c, currentGameFields, entry.roleLabel) || getPrimaryRoleColorForChar(c, currentGameFields), char: c });
        }
      });
    });
    playerMap.forEach(player => {
      player.specs.sort((a, b) => { const ia = ROLE_ORDER.indexOf(a.roleLabel); const ib = ROLE_ORDER.indexOf(b.roleLabel); return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib); });
    });
    return Array.from(playerMap.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  // --- HANDLERS ---
  function toggleDay(day: string) { setRecurringPatterns((prev: any) => { const next = { ...prev }; if (next[day]) { delete next[day]; } else { next[day] = { start: "20:00", end: "23:00" }; } return next; }); }
  function updateDayPattern(day: string, field: 'start'|'end', val: string) { setRecurringPatterns((prev: any) => ({ ...prev, [day]: { ...prev[day], [field]: val } })); }
  function updateSession(index: number, field: keyof SingleSessionConfig, val: string) { setSessionList((prev: any[]) => { const next = [...prev]; next[index] = { ...next[index], [field]: val }; return next; }); }
  function addSession() { setSessionList((prev: any[]) => { const last = prev[prev.length - 1]; return [...prev, { ...last }]; }); }
  function removeSession(index: number) { if (sessionList.length <= 1) return; setSessionList((prev: any[]) => prev.filter((_: any, i: number) => i !== index)); }

  function bumpSessionStart(index: number, deltaMinutes: number) {
    setSessionList((prev) => {
      const next = [...prev];
      const cur = next[index];
      if (!cur.date) {
        next[index] = { ...cur, startTime: adjustTime(cur.startTime, deltaMinutes) };
        return next;
      }
      const sh = shiftWallClock(cur.date, cur.startTime, deltaMinutes, userTz);
      next[index] = { ...cur, date: sh.date, startTime: sh.time };
      return next;
    });
  }

  function bumpSessionEnd(index: number, deltaMinutes: number) {
    setSessionList((prev) => {
      const next = [...prev];
      const cur = next[index];
      if (!cur.date) {
        next[index] = { ...cur, endTime: adjustTime(cur.endTime, deltaMinutes) };
        return next;
      }
      const sh = shiftWallClock(cur.date, cur.endTime, deltaMinutes, userTz);
      next[index] = { ...cur, endTime: sh.time };
      return next;
    });
  }

  function validateStep() {
      if (step === 0) {
          if (!guildId) return false;
          if (!raidOptionId) return false;
          
          if (scheduleMode === "series") {
              if (Object.keys(recurringPatterns).length === 0) return false;
          } else {
              // Single or Multi - validate sessionList
              const activeSessions = scheduleMode === "single" ? [sessionList[0]] : sessionList;
              if (activeSessions.some((s: any) => !s.date || !s.startTime || !s.endTime)) return false;
          }
          return true;
      }
      return true;
  }

  const [releaseOnCreate, setReleaseOnCreate] = useState(false);
  const getRaidLockoutWeek = () => {
    const s = sessionList[0];
    if (!s?.date) return null;
    const anchorTime = s.startTime || "12:00";
    return getResetWeekForDate(new Date(pickerPartsToUtc(s.date, anchorTime, userTz)), guildRule);
  };

  function handleSubmit(publish = false) {
      if (!selectedGuild) return;
      if (!validateStep()) return;
      
      const isRecurring = scheduleMode === "series";
      const finalSessions = (scheduleMode === "single" ? [sessionList[0]] : sessionList).map((s: any) => {
          const startIso = pickerPartsToUtc(s.date, s.startTime, userTz);
          const startDt = DateTime.fromISO(startIso, { zone: "utc" });
          let endDt = DateTime.fromISO(pickerPartsToUtc(s.date, s.endTime, userTz), { zone: "utc" });
          if (!startDt.isValid || !endDt.isValid) {
            const start = new Date(pickerPartsToUtc(s.date, s.startTime, userTz));
            let end = new Date(pickerPartsToUtc(s.date, s.endTime, userTz));
            if (end.getTime() <= start.getTime()) end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
            const durationMinutes = Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000));
            return { startsAt: start, durationMinutes };
          }
          if (endDt <= startDt) endDt = endDt.plus({ days: 1 });
          const durationMinutes = Math.max(1, Math.round(endDt.diff(startDt, "minutes").minutes));
          return { startsAt: startDt.toJSDate(), durationMinutes };
      });

      const values: CreateRaidFormValues = {
          guildId: selectedGuild.id,
          gameId: activeGameId,
          raidOptionId,
          raidLabel: raidLabel || (raidOptions.find((o: any) => o.id === raidOptionId)?.label ?? "Raid"),
          size,
          region: (selectedGuild.region || "EU") as "EU" | "US",
          realm: selectedGuild.serverName || "",
          raidLeaderCharacterId: raidLeaderId || null,
          lootMasterCharacterId: lootMasterId || null,
          metadata: { roster: { slots: rosterSlots, roles: rosterRoles, specs: rosterSlots.map((charId, idx) => { if (!charId) return null; const c = charById.get(charId); if (!c) return null; const entries = resolveSpecEntries(c, currentGameFields); const roleLabel = rosterRoles[idx]; const match = entries.find(e => e.roleLabel === roleLabel); return match ? match.specLabel : entries[0]?.specLabel || null; }), bench: benchSlots, benchRoles } , notes: notes.trim() },
          isRecurring,
          recurringPatterns: isRecurring ? recurringPatterns : undefined,
          sessions: !isRecurring ? finalSessions : undefined,
          softReserveEnabled,
          maxSoftReserves: softReserveEnabled ? maxSoftReserves : undefined,
          chainedRaidOptionIds: chainedRaidOptionIds.length > 0 ? chainedRaidOptionIds : undefined,
          publish,
      };
      // If the leader opted to release unassigned players for this raid's lockout week
      if (releaseOnCreate && selectedGuild) {
        const weekStart = getRaidLockoutWeek();
        if (weekStart) {
          fetch(`/api/guilds/${selectedGuild.id}/release`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ weekStart, released: true }),
          }).catch(err => console.error('Release toggle error:', err));
        }
      }
      onSubmit(values);
  }

  /*
   * Suggested raid times.
   *
   * This is memoised for responsiveness, not tidiness. It used to run inline
   * in the JSX, so it re-ran on EVERY render — including the one triggered by
   * clicking a suggestion. That put a full re-scan between the click and the
   * highlight, which is what made selecting a time feel sluggish.
   *
   * `sessionList` is deliberately NOT a dependency: picking a time changes
   * only which card is highlighted, never the analysis, so the click now hits
   * a warm cache and paints immediately.
   */
  const suggestionState = useMemo<SuggestionState>(() => {
    if (!schedulesLoaded || !currentGameFields) return { kind: "loading" };

    const raidInterestGroup = currentGameFields.find(
      (g: any) => g.key === "raid_interest" || g.key === "raid_preferences"
    );

    // 1. Identify users who have engaged with the system (set preferences on ANY character).
    // This allows us to be STRICT for them (if they didn't pick this raid, they aren't
    // interested) while being LENIENT for users who haven't set up preferences yet.
    // === CHARACTER-LEVEL FILTERING (mirrors benchAvailable logic exactly) ===
    const usersWithAnyRaidInterest = new Set<string>();
    if (raidInterestGroup) {
      guildRoster.forEach((c: any) => {
        const ownerId = c.userId || c.user_id || c.ownerId;
        if (!ownerId) return;
        if (Array.isArray(c.options) && c.options.some((opt: any) => Number(opt.group_id) === Number(raidInterestGroup.id))) {
          usersWithAnyRaidInterest.add(ownerId);
        }
      });
    }

    const eligibleChars = guildRoster.filter((c: any) => {
      if (c.isConfirmed === false) return false;
      if (c.isLockedOut === true) return false;
      if (raidOptionId && raidInterestGroup) {
        const ownerId = c.userId || c.user_id || c.ownerId;
        const userHasExpressedPreferences = ownerId && usersWithAnyRaidInterest.has(ownerId);
        const raidInterestOptions = Array.isArray(c.options) ? c.options.filter((opt: any) => Number(opt.group_id) === Number(raidInterestGroup.id)) : [];
        const allRequiredOptionIds = [raidOptionId, ...chainedRaidOptionIds];
        const hasInterestInAllRaids = allRequiredOptionIds.every((optId) =>
          raidInterestOptions.some((opt: any) => Number(opt.option_id) === Number(optId))
        );
        if (userHasExpressedPreferences && !hasInterestInAllRaids) return false;
      }
      if (!Array.isArray(c.ownerAvailabilitySlots) || c.ownerAvailabilitySlots.length !== 336) return false;
      return true;
    });

    // Deduplicate to one entry per PLAYER (first eligible char wins — availability is per-user anyway)
    const playerMap = new Map<string, { availability: boolean[]; schedule: { startsAt: string; durationMinutes: number }[] }>();
    eligibleChars.forEach((c: any) => {
      const ownerId = c.userId || c.user_id || c.ownerId;
      if (!ownerId || playerMap.has(ownerId)) return;
      playerMap.set(ownerId, { availability: c.ownerAvailabilitySlots, schedule: userSchedules[ownerId] || [] });
    });
    const interestedChars = Array.from(playerMap.entries()).map(([ownerId, data]) => ({
      ownerId,
      ownerAvailabilitySlots: data.availability,
      schedule: data.schedule,
    }));

    if (interestedChars.length === 0) return { kind: "no-signups" };

    // Count excluded players for the info line above suggestions
    const excludedLockedIds = new Set<string>();
    const excludedBusyIds = new Set<string>();
    const excludedUnconfirmedIds = new Set<string>();
    guildRoster.forEach((c: any) => {
      const ownerId = c.userId || c.user_id || c.ownerId;
      if (!ownerId) return;
      if (c.isLockedOut === true) excludedLockedIds.add(ownerId);
      if (c.isBusy === true) excludedBusyIds.add(ownerId);
      if (c.isConfirmed === false) excludedUnconfirmedIds.add(ownerId);
    });
    const eligibleUserIds = new Set(interestedChars.map((p) => p.ownerId));
    const lockedOutCount = Array.from(excludedLockedIds).filter((id) => !eligibleUserIds.has(id)).length;
    const busyUserCount = Array.from(excludedBusyIds).filter((id) => !eligibleUserIds.has(id) && !excludedLockedIds.has(id)).length;
    const unconfirmedCount = Array.from(excludedUnconfirmedIds).filter((id) => !eligibleUserIds.has(id) && !excludedLockedIds.has(id) && !excludedBusyIds.has(id)).length;

    const weekStartIso =
      DateTime.fromISO(getCurrentResetWeek(guildRule), { zone: "utc" })
        .plus({ days: suggestionsWeekOffset * 7 })
        .toISODate() || getCurrentResetWeek(guildRule);

    /*
     * Resolve every (day, slot) cell ONCE.
     *
     * A cell's bit index depends only on the cell and the timezone, never on
     * the player — but the old loop recomputed it inside the per-player loop,
     * so a 40-player roster did ~71,000 Luxon timezone conversions per render
     * instead of 336. Table first, then pure array lookups in the hot loop.
     */
    const cellBitIdx = new Int16Array(7 * SLOTS_PER_DAY);
    const cellStartMs = new Float64Array(7 * SLOTS_PER_DAY);
    for (let day = 0; day < 7; day++) {
      for (let slot = 0; slot < SLOTS_PER_DAY; slot++) {
        const bit = localCellToBitIdx(weekStartIso, day, slot, userTz);
        cellBitIdx[day * SLOTS_PER_DAY + slot] = bit;
        cellStartMs[day * SLOTS_PER_DAY + slot] = bitIdxToUtcInstant(weekStartIso, bit).getTime();
      }
    }

    // Bit indices are half-hour steps in UTC, so the window end is exact arithmetic.
    const windowMs = SUGGESTION_SLOTS * 30 * 60000;
    const nowMs = Date.now();
    const slotScores: { day: number; slot: number; available: number; total: number }[] = [];

    for (let day = 0; day < 7; day++) {
      const base = day * SLOTS_PER_DAY;
      for (let slot = 0; slot < SLOTS_PER_DAY - SUGGESTION_SLOTS; slot++) {
        const windowStartMs = cellStartMs[base + slot];

        // Skip slots whose start is already in the past. You can't plan a raid for
        // a time that already happened, and bench bilocation will (correctly) flag
        // every existing past-week assignment as a conflict.
        if (windowStartMs <= nowMs) continue;
        const windowEndMs = windowStartMs + windowMs;

        let availableCount = 0;
        for (const c of interestedChars) {
          const bits = c.ownerAvailabilitySlots;
          if (!bits || !Array.isArray(bits)) continue;

          let isAvailable = true;
          for (let s = 0; s < SUGGESTION_SLOTS; s++) {
            if (!bits[cellBitIdx[base + slot + s]]) { isAvailable = false; break; }
          }

          // Also check if busy with another raid at this time
          if (isAvailable && c.schedule && c.schedule.length > 0) {
            const busy = c.schedule.some((sess) => {
              const sStart = Date.parse(sess.startsAt);
              return sStart < windowEndMs && sStart + sess.durationMinutes * 60000 > windowStartMs;
            });
            if (busy) isAvailable = false;
          }

          if (isAvailable) availableCount++;
        }

        if (availableCount > 0) slotScores.push({ day, slot, available: availableCount, total: interestedChars.length });
      }
    }

    // Sort by availability count; each (day, slot) is already unique — show top 5 starts
    slotScores.sort((a, b) => b.available - a.available);
    const topSlots = slotScores.slice(0, 5);

    if (topSlots.length === 0) {
      // If the entire shown week is already in the past, the scoring loop filtered
      // every slot. Nudge the user toward the "next reset" toggle instead of the
      // misleading "members need to set their availability" message.
      const weekEndLocal = DateTime.fromISO(weekStartIso, { zone: "utc" }).plus({ days: 7 }).setZone(userTz);
      return { kind: "empty", isWeekEntirelyPast: weekEndLocal.isValid && weekEndLocal.toMillis() <= Date.now() };
    }

    // Only the five survivors need date formatting.
    const slots: RaidTimeSuggestion[] = topSlots.map((s) => {
      const startLocal = DateTime.fromMillis(cellStartMs[s.day * SLOTS_PER_DAY + s.slot], { zone: "utc" }).setZone(userTz);
      const endLocal = startLocal.plus({ minutes: SUGGESTION_SLOTS * 30 });
      return {
        day: s.day,
        dayLabel: DAYS[s.day],
        timeStr: startLocal.toFormat("HH:mm"),
        available: s.available,
        total: s.total,
        pct: Math.round((s.available / s.total) * 100),
        startMillis: startLocal.toMillis(),
        date: startLocal.toFormat("yyyy-MM-dd"),
        startTime: startLocal.toFormat("HH:mm"),
        endTime: endLocal.toFormat("HH:mm"),
      };
    });

    return { kind: "ready", slots, lockedOutCount, busyUserCount, unconfirmedCount, excludedCount: lockedOutCount + busyUserCount + unconfirmedCount };
  }, [schedulesLoaded, currentGameFields, guildRoster, userSchedules, raidOptionId, chainedRaidOptionIds, suggestionsWeekOffset, userTz, guildRule]);

  /* Local start of the current selection, for highlighting the matching card. */
  const selectedStartMillis = useMemo(() => {
    const active = sessionList[0];
    if (!active?.date || !active?.startTime) return null;
    const dt = DateTime.fromISO(`${active.date}T${active.startTime}`, { zone: userTz });
    return dt.isValid ? dt.toMillis() : null;
  }, [sessionList, userTz]);

  const progressPct = ((step + 1) / STEP_CONFIG.length) * 100;

  // `themeClass` scopes the palette to this card: every color below resolves
  // from CSS variables defined in theme/themes.css, so the app around it is
  // unaffected and switching palettes is a class swap on this one element.
  return (
    <div className={`${themeClass} relative flex w-full flex-col overflow-hidden rounded-2xl border border-[var(--c-line)] bg-[var(--c-bg)] shadow-[var(--c-shadow-lg)] h-[calc(100vh-160px)] animate-in fade-in zoom-in-95 duration-300`} ref={rootRef}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] h-[500px] w-[500px] rounded-full bg-[var(--c-wash-1)] blur-[100px]" />
        <div className="absolute bottom-[-10%] right-[-10%] h-[500px] w-[500px] rounded-full bg-[var(--c-wash-2)] blur-[100px]" />
      </div>

      {/* HEADER */}
      <div className="relative z-10 flex shrink-0 flex-col gap-4 border-b border-[var(--c-line)] bg-[var(--c-sunken)] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-bold tracking-tight text-[var(--c-ink)]">
                {scheduleMode === "series" ? "Create Recurring Series" : scheduleMode === "multi" ? "Create Multi-Day Operation" : "Create Raid"}
            </h2>
            <div className="text-xs text-[var(--c-ink-3)]">Configure your raid and build your roster.</div>
          </div>
          <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-[var(--c-surface)] rounded-full p-1 border border-[var(--c-line)]">
            {STEP_CONFIG.map((s, idx) => {
              const active = idx === step;
              const done = idx < step;
              return (
                <button key={s.id} onClick={() => idx < step && setStep(idx as any)} className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold transition-all ${active ? "bg-[var(--c-accent-soft)] text-[var(--c-accent-ink)] border border-[var(--c-accent-line)] shadow-[var(--c-shadow-sm)]" : done ? "text-[var(--c-good-ink)] hover:bg-[var(--c-sunken)] cursor-pointer" : "text-[var(--c-ink-4)]"}`}>
                  <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${active ? "bg-[var(--c-accent)] text-[var(--c-ink-on-fill)]" : done ? "bg-[var(--c-good)] text-[var(--c-ink-on-fill)]" : "bg-[var(--c-sunken-2)] text-[var(--c-ink-3)]"}`}>{idx + 1}</span>
                  {s.label}
                </button>
              );
            })}
          </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 h-[2px] w-full bg-[var(--c-sunken)]">
          <div className="h-full bg-[var(--c-accent)] transition-all duration-500 ease-out" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* CONTENT */}
      <div className="relative z-10 flex-1 overflow-hidden">
        {step === 0 && (
          <div className="h-full overflow-y-auto p-8">
            <div className="mx-auto max-w-5xl space-y-10">
              <div className="space-y-4">
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-3)] pl-1">Guild</label>
                {guilds.length === 0 ? (
                  <div className="rounded-xl border border-[var(--c-warn-line)] bg-[var(--c-warn-soft)] p-6 text-center">
                    <div className="text-[var(--c-warn-ink)] font-semibold mb-2">No Eligible Guilds</div>
                    <div className="text-sm text-[var(--c-ink-2)]">
                      To create a raid, you must be the <span className="text-[var(--c-ink)] font-medium">owner</span> or an <span className="text-[var(--c-ink)] font-medium">officer</span> of a guild.
                    </div>
                    <div className="text-xs text-[var(--c-ink-3)] mt-3">
                      Create a guild in the Guilds tab, or ask a guild leader to promote you to officer.
                    </div>
                  </div>
                ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {guilds.map((g) => {
                    const isSel = g.id === guildId;
                    const fullLogoUrl = g.logoUrl ? (g.logoUrl.startsWith("http") ? g.logoUrl : `${AUTH_BASE_URL}${g.logoUrl}`) : null;
                    return (
                      <button key={g.id} onClick={() => setGuildId(g.id)} className={`group relative flex items-center gap-4 rounded-xl border p-4 text-left transition-all duration-200 ${isSel ? "border-[var(--c-accent)] bg-[var(--c-accent-soft)] shadow-[var(--c-shadow)] ring-1 ring-[var(--c-accent)]" : "border-[var(--c-line)] bg-[var(--c-sunken)] hover:bg-[var(--c-sunken-2)] hover:border-[var(--c-line-strong)]"}`}>
                        <div className={`h-12 w-12 shrink-0 rounded-lg bg-[var(--c-surface)] flex items-center justify-center font-bold text-xl overflow-hidden border border-[var(--c-line)] ${isSel ? "text-[var(--c-accent-ink)]" : "text-[var(--c-ink-4)]"}`}>
                          {fullLogoUrl ? <img src={fullLogoUrl} alt={g.name} className="h-full w-full object-cover" /> : g.name.charAt(0)}
                        </div>
                        <div>
                          <div className={`text-sm font-bold ${isSel ? "text-[var(--c-ink)]" : "text-[var(--c-ink-2)]"}`}>{g.name}</div>
                          <div className="text-[11px] font-medium text-[var(--c-ink-3)]">{g.region} · {g.serverName || "Unknown"}</div>
                        </div>
                        {isSel && <div className="absolute right-4 top-4 h-2.5 w-2.5 rounded-full bg-[var(--c-accent)] shadow-[var(--c-glow-accent)] animate-pulse" />}
                      </button>
                    );
                  })}
                  </div>
                  )}

                {/* Which game this raid is for. Only shown when the guild plays
                    more than one (e.g. Anniversary + WoW Forever). */}
                {guildGames.length > 1 && (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="pl-1 text-xs font-bold uppercase tracking-widest text-[var(--c-ink-3)]">Game</span>
                    {guildGames.map((g) => {
                      const sel = g.gameId === activeGameId;
                      return (
                        <button
                          key={g.gameId}
                          type="button"
                          onClick={() => {
                            if (sel) return;
                            setRaidGameId(g.gameId);
                            // The previous game's instance does not exist in this one.
                            setRaidOptionId(null);
                            setChainedRaidOptionIds([]);
                            setRaidLabel("");
                          }}
                          className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-all ${sel ? "border-[var(--c-accent)] bg-[var(--c-accent-soft)] text-[var(--c-accent-ink)]" : "border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"}`}
                        >
                          {g.gameName}
                        </button>
                      );
                    })}
                  </div>
                )}
                </div>

              <div className="grid gap-10 md:grid-cols-2">
                <div className="space-y-4">
                  <label className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-3)] pl-1">Raid Details</label>
                  <div className="space-y-3">
                    <div className="relative">
                    <select value={raidOptionId ?? ""} onChange={(e) => { const val = Number(e.target.value); const opt = raidOptions.find((o: any) => o.id === val); setRaidOptionId(val); setChainedRaidOptionIds([]); if (opt) { setRaidLabel(opt.label); if (opt.metadata?.size) setSize(opt.metadata.size as RaidSize); } }} className="w-full appearance-none rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-4 py-3.5 text-sm text-[var(--c-ink)] focus:border-[var(--c-accent)] focus:outline-none transition-colors hover:bg-[var(--c-surface)]">
                        <option value="">Select Raid Template...</option>
                        {raidOptions.map((opt: any) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                      </select>
                      <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--c-ink-3)]">▼</div>
                    </div>
                    
                    {/* ── Raid Chaining UI ── */}
                    {raidOptionId && (() => {
                      const primaryOpt = raidOptions.find((o: any) => o.id === raidOptionId);
                      const primarySize = primaryOpt?.metadata?.size;
                      // Eligible options: same size, different from primary, not already chained
                      const eligibleForChain = raidOptions.filter((o: any) => 
                        o.id !== raidOptionId && 
                        !chainedRaidOptionIds.includes(o.id) && 
                        o.metadata?.size === primarySize
                      );
                      const canAddMore = chainedRaidOptionIds.length < 2 && eligibleForChain.length > 0;
                      
                      return (
                        <div className="space-y-2">
                          {/* Show chained raids as pills */}
                          {chainedRaidOptionIds.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {chainedRaidOptionIds.map(optId => {
                                const opt = raidOptions.find((o: any) => o.id === optId);
                                return (
                                  <div key={optId} className="flex items-center gap-2 rounded-lg border border-[var(--c-accent-line)] bg-[var(--c-accent-soft)] px-3 py-1.5 text-sm">
                                    <svg className="w-3.5 h-3.5 text-[var(--c-accent-ink)] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                    </svg>
                                    <span className="text-[var(--c-accent-ink)] font-medium">{opt?.label || `Raid #${optId}`}</span>
                                    <button 
                                      onClick={() => setChainedRaidOptionIds(prev => prev.filter(id => id !== optId))}
                                      className="text-[var(--c-accent-ink)] hover:text-[var(--c-bad-ink)] transition-colors ml-1"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Add chain button + dropdown */}
                          {canAddMore && (
                            <div className="relative">
                              <select 
                                value="" 
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  if (val && !chainedRaidOptionIds.includes(val)) {
                                    setChainedRaidOptionIds(prev => [...prev, val]);
                                  }
                                }}
                                className="w-full appearance-none rounded-lg border border-dashed border-[var(--c-accent-line)] bg-[var(--c-accent-soft)] px-4 py-2.5 text-sm text-[var(--c-accent-ink)] focus:border-[var(--c-accent)] focus:outline-none transition-colors hover:bg-[var(--c-accent-soft)] cursor-pointer"
                              >
                                <option value="" className="bg-[var(--c-bg)] text-[var(--c-ink-2)]">+ Link Another Raid...</option>
                                {eligibleForChain.map((opt: any) => (
                                  <option key={opt.id} value={opt.id} className="bg-[var(--c-bg)] text-[var(--c-ink)]">{opt.label} ({opt.metadata?.size}-man)</option>
                                ))}
                              </select>
                              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--c-accent-ink)]">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                              </div>
                            </div>
                          )}
                          
                          {/* Chain info tooltip */}
                          {chainedRaidOptionIds.length > 0 && (
                            <div className="text-[10px] text-[var(--c-accent-ink)] pl-1">
                              Linked raids share one roster, one session, and one soft reserve pool.
                              Players locked to any raid in the chain are excluded.
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    
                    <input value={raidLabel} onChange={(e) => setRaidLabel(e.target.value)} placeholder="Custom Label (e.g. 'Progression Night')" className="w-full rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-4 py-3.5 text-sm text-[var(--c-ink)] placeholder:text-[var(--c-ink-4)] focus:border-[var(--c-accent)] focus:outline-none transition-colors hover:bg-[var(--c-surface)]" />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between pl-1">
                    <label className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-3)]">Schedule & Sessions</label>
                    <PickerTzLabel title="Date and times use this timezone. Submitted sessions are stored as UTC." />
                  </div>
                  
                  {/* --- 3-WAY TAB SWITCHER --- */}
                  <div className="flex bg-[var(--c-surface)] border border-[var(--c-line)] p-1 rounded-xl mb-3">
                        <button onClick={() => setScheduleMode("single")} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${scheduleMode === "single" ? "bg-[var(--c-sunken-2)] text-[var(--c-ink)] shadow-[var(--c-shadow-sm)] border border-[var(--c-line)]" : "text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"}`}>
                            Single Event
                        </button>
                        <button onClick={() => setScheduleMode("multi")} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${scheduleMode === "multi" ? "bg-[var(--c-accent-soft)] text-[var(--c-accent-ink)] border border-[var(--c-accent-line)]" : "text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"}`}>
                            Multi-Day
                        </button>
                        <button onClick={() => setScheduleMode("series")} className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${scheduleMode === "series" ? "bg-[var(--c-squad-soft)] text-[var(--c-squad-ink)] border border-[var(--c-squad-line)]" : "text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"}`}>
                            Recurring Series
                        </button>
                  </div>

                  <div className="space-y-3">
                    {/* MODE 1: SINGLE EVENT (Clean Original Style) */}
                    {scheduleMode === "single" && (
                        <div className="animate-in fade-in space-y-3">
                            <input style={{ colorScheme: 'dark' }} type="date" value={sessionList[0].date} onChange={(e) => updateSession(0, 'date', e.target.value)} className="w-full rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-4 py-3.5 text-sm text-[var(--c-ink)] focus:border-[var(--c-accent)] focus:outline-none transition-colors hover:bg-[var(--c-surface)]" />
                            <div className="flex gap-4">
                                <div className="flex flex-1 items-center gap-1 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] p-1.5">
                                    <button onClick={() => bumpSessionStart(0, -30)} className="h-9 w-9 rounded-lg bg-[var(--c-sunken)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] flex items-center justify-center">-</button>
                                    <input style={{ colorScheme: 'dark' }} type="time" value={sessionList[0].startTime} onChange={(e) => updateSession(0, 'startTime', snapToHalfHour(e.target.value))} className="flex-1 bg-transparent text-center text-sm font-bold text-[var(--c-ink)] focus:outline-none" />
                                    <button onClick={() => bumpSessionStart(0, 30)} className="h-9 w-9 rounded-lg bg-[var(--c-sunken)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] flex items-center justify-center">+</button>
                                </div>
                                <span className="flex items-center text-[var(--c-ink-4)] font-bold">➕</span>
                                <div className="flex flex-1 items-center gap-1 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] p-1.5">
                                    <button onClick={() => bumpSessionEnd(0, -30)} className="h-9 w-9 rounded-lg bg-[var(--c-sunken)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] flex items-center justify-center">-</button>
                                    <input style={{ colorScheme: 'dark' }} type="time" value={sessionList[0].endTime} onChange={(e) => updateSession(0, 'endTime', snapToHalfHour(e.target.value))} className="flex-1 bg-transparent text-center text-sm font-bold text-[var(--c-ink)] focus:outline-none" />
                                    <button onClick={() => bumpSessionEnd(0, 30)} className="h-9 w-9 rounded-lg bg-[var(--c-sunken)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] flex items-center justify-center">+</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* MODE 2: MULTI-NIGHT (Updated to match Series Style) */}
                    {scheduleMode === "multi" && (
                        <div className="animate-in fade-in space-y-3">
                            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-white/10">
                                {sessionList.map((session: any, idx: number) => (
                                    <div key={idx} className="grid grid-cols-[130px_1fr_auto_1fr_auto] items-center gap-2 bg-[var(--c-sunken)] border border-[var(--c-line)] rounded-lg p-2">
                                        
                                        {/* Col 1: Date Input */}
                                        <input 
                                            style={{ colorScheme: 'dark' }}
                                            type="date" 
                                            value={session.date} 
                                            onChange={(e) => updateSession(idx, 'date', e.target.value)} 
                                            className="bg-[var(--c-sunken)] border border-[var(--c-line)] rounded px-2 py-1 text-xs text-[var(--c-ink)] focus:outline-none focus:border-[var(--c-accent)]"
                                        />

                                        {/* Col 2: Start Stepper */}
                                        <div className="flex items-center gap-1 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-1 min-w-0">
                                            <button onClick={() => bumpSessionStart(idx, -30)} className="h-6 w-6 rounded bg-[var(--c-sunken)] text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink)] flex-shrink-0 flex items-center justify-center">-</button>
                                            <input value={session.startTime} readOnly className="flex-1 bg-transparent text-center text-xs font-bold text-[var(--c-ink)] focus:outline-none min-w-0" />
                                            <button onClick={() => bumpSessionStart(idx, 30)} className="h-6 w-6 rounded bg-[var(--c-sunken)] text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink)] flex-shrink-0 flex items-center justify-center">+</button>
                                        </div>

                                        <span className="text-[var(--c-ink-4)] text-xs px-1">to</span>

                                        {/* Col 4: End Stepper */}
                                        <div className="flex items-center gap-1 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-1 min-w-0">
                                            <button onClick={() => bumpSessionEnd(idx, -30)} className="h-6 w-6 rounded bg-[var(--c-sunken)] text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink)] flex-shrink-0 flex items-center justify-center">-</button>
                                            <input value={session.endTime} readOnly className="flex-1 bg-transparent text-center text-xs font-bold text-[var(--c-ink)] focus:outline-none min-w-0" />
                                            <button onClick={() => bumpSessionEnd(idx, 30)} className="h-6 w-6 rounded bg-[var(--c-sunken)] text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink)] flex-shrink-0 flex items-center justify-center">+</button>
                                        </div>

                                        {/* Col 5: Remove */}
                                        {sessionList.length > 1 && (
                                            <button onClick={() => removeSession(idx)} className="h-6 w-6 flex items-center justify-center rounded text-[var(--c-ink-4)] hover:text-[var(--c-bad-ink)] transition-colors">✕</button>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <button onClick={addSession} className="w-full py-2 border border-dashed border-[var(--c-line)] rounded-lg text-xs font-bold text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:border-[var(--c-line-strong)] transition-all">
                                + Add Session (Multi-Day)
                            </button>
                        </div>
                    )}

                    {/* MODE 3: SERIES (Recurring Pattern) */}
                    {scheduleMode === "series" && (
                        <div className="space-y-4 animate-in fade-in">
                            <div className="flex justify-between gap-1">
                                {DAYS.map(day => {
                                    const isActive = !!recurringPatterns[day];
                                    return (
                                        <button key={day} onClick={() => toggleDay(day)} className={`flex-1 py-2 rounded-lg text-[10px] font-bold uppercase transition-all border ${isActive ? "bg-[var(--c-squad-soft)] border-[var(--c-squad)] text-[var(--c-squad-ink)]" : "bg-[var(--c-sunken)] border-[var(--c-line-soft)] text-[var(--c-ink-4)] hover:bg-[var(--c-sunken)]"}`}>
                                            {day}
                                        </button>
                                    )
                                })}
                            </div>
                            <div className="space-y-2 border border-[var(--c-line-soft)] rounded-xl bg-[var(--c-sunken)] p-2">
                                {Object.keys(recurringPatterns).length === 0 && (
                                    <div className="text-center text-xs text-[var(--c-ink-4)] py-4 italic">Select days above to configure times.</div>
                                )}
                                {DAYS.map(day => {
                                    const pattern = recurringPatterns[day];
                                    if (!pattern) return null;
                                    return (
                                        <div key={day} className="grid grid-cols-[2rem_1fr_auto_1fr] items-center gap-2 bg-[var(--c-sunken)] border border-[var(--c-line)] rounded-lg p-2 animate-in slide-in-from-left-2">
                                            <div className="text-center text-xs font-bold text-[var(--c-squad-ink)]">{day}</div>
                                            <div className="flex items-center gap-1 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-1 min-w-0">
                                                <button onClick={() => updateDayPattern(day, 'start', adjustTime(pattern.start, -30))} className="h-6 w-6 rounded bg-[var(--c-sunken)] text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink)] flex-shrink-0 flex items-center justify-center">-</button>
                                                <input value={pattern.start} readOnly className="flex-1 bg-transparent text-center text-xs font-bold text-[var(--c-ink)] focus:outline-none min-w-0" />
                                                <button onClick={() => updateDayPattern(day, 'start', adjustTime(pattern.start, 30))} className="h-6 w-6 rounded bg-[var(--c-sunken)] text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink)] flex-shrink-0 flex items-center justify-center">+</button>
                                            </div>
                                            <span className="text-[var(--c-ink-4)] text-xs px-1">to</span>
                                            <div className="flex items-center gap-1 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] p-1 min-w-0">
                                                <button onClick={() => updateDayPattern(day, 'end', adjustTime(pattern.end, -30))} className="h-6 w-6 rounded bg-[var(--c-sunken)] text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink)] flex-shrink-0 flex items-center justify-center">-</button>
                                                <input value={pattern.end} readOnly className="flex-1 bg-transparent text-center text-xs font-bold text-[var(--c-ink)] focus:outline-none min-w-0" />
                                                <button onClick={() => updateDayPattern(day, 'end', adjustTime(pattern.end, 30))} className="h-6 w-6 rounded bg-[var(--c-sunken)] text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink)] flex-shrink-0 flex items-center justify-center">+</button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-3)] pl-1">Suggested Raid Times</label>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { setSchedulesLoaded(false); setSuggestionsWeekOffset(o => Math.max(0, o - 1)); }}
                        disabled={suggestionsWeekOffset === 0}
                        className="p-0.5 rounded border border-[var(--c-line)] bg-[var(--c-sunken)] hover:bg-[var(--c-sunken-2)] text-[var(--c-ink-3)] hover:text-[var(--c-ink)] transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                      </button>
                      <span className="px-2 py-0.5 rounded bg-[var(--c-sunken)] border border-[var(--c-line)] text-[10px] font-bold text-[var(--c-ink-2)] min-w-[100px] text-center">
                        {(() => {
                          // Render the label through the rule: a US week starts on Tuesday.
                          const label = DateTime.fromISO(getCurrentResetWeek(guildRule), { zone: 'utc' })
                            .plus({ days: suggestionsWeekOffset * 7 })
                            .toISODate() || getCurrentResetWeek(guildRule);
                          return formatWeekRange(label, guildRule);
                        })()}
                      </span>
                      <button
                        onClick={() => { setSchedulesLoaded(false); setSuggestionsWeekOffset(o => Math.min(1, o + 1)); }}
                        disabled={suggestionsWeekOffset >= 1}
                        className="p-0.5 rounded border border-[var(--c-line)] bg-[var(--c-sunken)] hover:bg-[var(--c-sunken-2)] text-[var(--c-ink-3)] hover:text-[var(--c-ink)] transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--c-good-soft)] border border-[var(--c-good-line)]">
                    <span className="text-xs font-bold text-[var(--c-good-ink)]">{size}</span>
                    <span className="text-[10px] text-[var(--c-good-ink)] uppercase">Players</span>
                  </div>
                </div>

                {suggestionState.kind === "loading" ? (
                  <div className="p-6 rounded-xl border border-[var(--c-line)] bg-[var(--c-sunken)] text-center animate-pulse">
                    <div className="text-[var(--c-ink-3)] text-sm">Analyzing availability...</div>
                  </div>
                ) : suggestionState.kind === "no-signups" ? (
                  <div className="p-6 rounded-xl border border-[var(--c-line)] bg-[var(--c-sunken)] text-center">
                    <div className="text-[var(--c-ink-3)] text-sm">No guild members have signed up for this raid yet.</div>
                    <div className="text-[var(--c-ink-4)] text-xs mt-1">Members can express interest in their roster settings.</div>
                  </div>
                ) : suggestionState.kind === "empty" ? (
                  <div className="p-6 rounded-xl border border-[var(--c-line)] bg-[var(--c-sunken)] text-center">
                    <div className="text-[var(--c-ink-3)] text-sm">
                      {suggestionState.isWeekEntirelyPast
                        ? 'This reset week has already ended.'
                        : suggestionsWeekOffset === 0
                          ? 'No upcoming slots left this reset.'
                          : 'No common availability found.'}
                    </div>
                    <div className="text-[var(--c-ink-4)] text-xs mt-1">
                      {suggestionsWeekOffset === 0
                        ? 'Try the next reset week.'
                        : 'Guild members need to set their availability.'}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {suggestionState.excludedCount > 0 && (
                      <div className="flex flex-wrap items-center gap-3 px-1 mb-1">
                        {suggestionState.lockedOutCount > 0 && (
                          <span className="text-[10px] text-[var(--c-squad-ink)] font-medium">{suggestionState.lockedOutCount} raid locked</span>
                        )}
                        {suggestionState.busyUserCount > 0 && (
                          <span className="text-[10px] text-[var(--c-warn-ink)] font-medium">{suggestionState.busyUserCount} in another raid</span>
                        )}
                        {suggestionState.unconfirmedCount > 0 && (
                          <span className="text-[10px] text-[var(--c-ink-4)] font-medium">{suggestionState.unconfirmedCount} unconfirmed</span>
                        )}
                      </div>
                    )}
                    {suggestionState.slots.map((s) => {
                      const isSelected = selectedStartMillis === s.startMillis;
                      return (
                        <button
                          key={s.startMillis}
                          onClick={() => {
                            // Pre-formatted in the memo, so selecting is a plain
                            // state write with no date math between click and paint.
                            setSessionList([{ date: s.date, startTime: s.startTime, endTime: s.endTime }]);
                            setScheduleMode('single');
                          }}
                          className={`w-full p-3 rounded-xl border transition-all text-left flex items-center gap-4 ${
                            isSelected
                              ? 'border-[var(--c-accent)] bg-[var(--c-accent-soft)] ring-1 ring-[var(--c-accent)]'
                              : 'border-[var(--c-line)] bg-[var(--c-sunken)] hover:bg-[var(--c-sunken)] hover:border-[var(--c-line-strong)]'
                          }`}
                        >
                          <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[var(--c-sunken)] flex flex-col items-center justify-center">
                            <div className="text-[10px] font-bold text-[var(--c-ink-3)] uppercase">{s.dayLabel}</div>
                            <div className="text-sm font-black text-[var(--c-ink)]">{s.timeStr}</div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-[var(--c-ink)]">{s.available}</span>
                              <span className="text-xs text-[var(--c-ink-3)]">of {s.total} available</span>
                            </div>
                            <div className="mt-1 h-1.5 rounded-full bg-[var(--c-sunken-2)] overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${s.pct >= 80 ? 'bg-[var(--c-good)]' : s.pct >= 50 ? 'bg-[var(--c-warn)]' : 'bg-[var(--c-bad)]'}`}
                                style={{ width: `${s.pct}%` }}
                              />
                            </div>
                          </div>
                          <div className={`text-lg font-black ${s.pct >= 80 ? 'text-[var(--c-good-ink)]' : s.pct >= 50 ? 'text-[var(--c-warn-ink)]' : 'text-[var(--c-bad-ink)]'}`}>
                            {s.pct}%
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* --- STEP 1: ROSTER --- */}
        {step === 1 && (
          <div className="grid h-full grid-cols-[450px_1fr] min-[1800px]:grid-cols-[760px_1fr] divide-x divide-[var(--c-line)]">
          {/* LEFT: BENCH (2-Column Grid) */}
          <div className="flex flex-col bg-[var(--c-sunken)] h-full overflow-hidden">
              <div className="flex items-center justify-between border-b border-[var(--c-line)] p-5 bg-[var(--c-sunken)]">
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-2)]">Roster Pool</div>
                <div className="rounded-full bg-[var(--c-sunken-2)] px-2 py-0.5 text-[10px] font-bold text-[var(--c-ink)]">{playerBenchCards.length} Players</div>
              </div>
              
              {/* Force custom scrollbar styles via raw CSS to ensure it works regardless of Tailwind config */}
              <style>{`
                .bench-scrollbar::-webkit-scrollbar { width: 5px; }
                .bench-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .bench-scrollbar::-webkit-scrollbar-thumb { background: var(--c-line); border-radius: 9999px; }
                .bench-scrollbar::-webkit-scrollbar-thumb:hover { background: var(--c-line-strong); }
              `}</style>
              <div className="flex-1 overflow-y-auto p-5 space-y-4 pr-2 bench-scrollbar" style={{ scrollbarWidth: 'thin', scrollbarColor: 'var(--c-sunken-2) transparent' }}>
                {/* Role filter tabs */}
                <div className="flex gap-1 flex-wrap mb-2">
                  <button onClick={() => setBenchRoleFilter(null)} className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase transition-all border ${!benchRoleFilter ? 'bg-[var(--c-sunken-2)] border-[var(--c-line-strong)] text-[var(--c-ink)]' : 'border-[var(--c-line-soft)] text-[var(--c-ink-4)] hover:text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)]'}`}>All ({playerBenchCards.length})</button>
                  {sortedRoles.map(role => {
                    const count = playerBenchCards.filter(p => p.specs.some(s => s.roleLabel === role)).length;
                    return (
                      <button key={role} onClick={() => setBenchRoleFilter(benchRoleFilter === role ? null : role)} className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase transition-all border flex items-center gap-1 ${benchRoleFilter === role ? 'bg-[var(--c-sunken-2)] border-[var(--c-line-strong)] text-[var(--c-ink)]' : 'border-[var(--c-line-soft)] text-[var(--c-ink-4)] hover:text-[var(--c-ink-3)] hover:bg-[var(--c-sunken)]'}`}>
                        <span className="text-xs">{getRoleIcon(role)}</span>
                        <span>{role === "Melee DPS" ? "Melee" : role === "Ranged DPS" ? "Ranged" : role}</span>
                        <span className="opacity-50">({count})</span>
                      </button>
                    );
                  })}
                </div>

                {/*
                  Two columns above 1800px, stacked below — see the breakpoint note
                  at the top of the file. `content-start` stops a short column from
                  stretching its cards to fill the taller one beside it.
                */}
                <div className={`grid gap-3 ${benchColumns.length > 1 ? "grid-cols-1 min-[1800px]:grid-cols-2" : "grid-cols-1"}`}>
                  {benchColumns.map((benchColumn, benchColIdx) => (
                    <div key={benchColIdx} className="flex flex-wrap items-stretch content-start gap-2">
                      {benchColumn.map(({ player, visibleSpecs }) => {
                        const isHoveredPlayer = hoveredOwnerKey && player.ownerKey && hoveredOwnerKey === player.ownerKey;
                        const isSquadmateHovered = !!(hoveredSquadId && player.squadInfo?.squadId === hoveredSquadId && hoveredOwnerKey !== player.ownerKey);
                        const filteredSpecs = visibleSpecs;
                        // Few enough specs to share a row with the next player.
                        const isNarrow = filteredSpecs.length <= BENCH_PAIR_MAX_SPECS;

                        /*
                         * Icon-only tiles don't say which character they belong to, which
                         * is invisible for a one-character player but ambiguous the moment
                         * someone brings alts. Hovering a tile resolves the card's name and
                         * class colour to that character, so the header answers "whose spec
                         * is this?" without needing the tooltip.
                         *
                         * Note this is NOT gated on the character being an alt: hovering the
                         * primary's own spec still tints, otherwise the name would colour for
                         * some tiles and not others. The name only *changes* for an alt; the
                         * tint applies to every hover, and the resting state stays plain ink.
                         *
                         * `hoveredCharacterId` is global (one tile hovered at a time), so the
                         * lookup misses on every other card and they keep their own name.
                         */
                        const hoveredSpec = hoveredCharacterId
                          ? filteredSpecs.find((s) => s.charId === hoveredCharacterId)
                          : undefined;
                        const displayCharName = hoveredSpec ? hoveredSpec.charName : player.primaryCharName;
                        const isAltShown = !!hoveredSpec && hoveredSpec.charName !== player.primaryCharName;

                        return (
                          <div
                            key={player.userId}
                            className={`flex-none rounded-lg border p-3 transition-all ${isNarrow ? 'w-[calc(50%-4px)]' : 'w-full'} ${isSquadmateHovered ? 'border-[var(--c-squad-line)] bg-[var(--c-squad-soft)]' : isHoveredPlayer ? 'border-[var(--c-line-strong)] bg-[var(--c-sunken-2)]' : 'border-[var(--c-line)] bg-[var(--c-surface)] hover:bg-[var(--c-sunken-2)]'}`}
                            style={isSquadmateHovered ? { outline: '2px dashed var(--c-squad)', outlineOffset: '2px' } : undefined}
                            onMouseEnter={() => { if (player.ownerKey) setHoveredOwnerKey(player.ownerKey); if (player.squadInfo) setHoveredSquadId(player.squadInfo.squadId); }}
                            onMouseLeave={() => { setHoveredOwnerKey(null); setHoveredSquadId(null); setHoveredCharacterId(null); setHoveredSpecTags(new Set()); }}
                          >
                            {/* Player name row */}
                            <div className="flex items-center gap-2 mb-2">
                              <span
                                className="text-sm font-bold truncate"
                                title={isAltShown ? `${displayCharName} (${hoveredSpec!.className})` : undefined}
                              >
                                {/* Two different transitions, because two different things change:
                                    - name changes (alt hovered) -> `key` remounts the span and
                                      replays the fade, so the text doesn't pop.
                                    - only the colour changes (primary's own spec hovered) -> no
                                      remount, so transition-colors eases the tint in.
                                    Each case is covered by exactly one of them. */}
                                <span
                                  key={displayCharName}
                                  className="lfm-fade-swap transition-colors duration-200"
                                  style={{ color: hoveredSpec ? onLight(CLASS_COLORS[hoveredSpec.className] || '#9d9d9d') : 'var(--c-ink)' }}
                                >
                                  {displayCharName}
                                </span>
                                {player.charCount > 1 && <span className="text-[var(--c-ink-3)] font-normal"> & {player.charCount - 1} more</span>}
                              </span>
                              {player.squadInfo && (
                                <span className="flex items-center gap-0.5 text-[9px] text-[var(--c-squad-ink)] bg-[var(--c-squad-soft)] px-1 py-0.5 rounded" title={player.squadInfo.squadName}>
                                  <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                  </svg>
                                  <span className="font-bold">{player.squadInfo.squadSize}</span>
                                </span>
                              )}
                            </div>
                        
                            {/* Spec icons - each is draggable/clickable. Icon-only so a
                                player's specs read at a glance and fit on one row; the
                                spec name, character and parse live in the tooltip. */}
                            <div className="flex flex-wrap gap-1.5">
                              {filteredSpecs.map((spec) => {
                                const isDup = hoveredCharacterId === spec.charId;
                                const iconUrl = getSpecIconUrl(spec.specLabel, spec.className, currentGameFields);
                                const wclScore = (spec.char as any)?.metadata?.wcl_perf?.score;
                                const hasScore = typeof wclScore === 'number';
                                // Icon-only tiles carry no visible text, so everything the old
                                // chip spelled out has to live in the tooltip and the a11y label.
                                const specTitle = `${spec.specLabel} · ${spec.charName} (${spec.className})${hasScore ? ` · parse ${wclScore}` : ''}`;

                                return (
                                  <button
                                    key={`${spec.charId}-${spec.specLabel}`}
                                    title={specTitle}
                                    aria-label={specTitle}
                                    className={`group relative flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 transition-all cursor-grab active:cursor-grabbing select-none ${isDup ? 'ring-2 ring-[var(--c-warn)] scale-110 z-10' : 'hover:scale-110 hover:shadow-[var(--c-shadow)] hover:z-10'}`}
                                    style={{
                                      borderColor: spec.roleColor ? `${onLightAccent(spec.roleColor)}99` : 'var(--c-line)',
                                      backgroundColor: isDup && spec.roleColor ? softBgFromHex(onLightAccent(spec.roleColor), 0.22) : spec.roleColor ? `${onLightAccent(spec.roleColor)}1a` : 'var(--c-sunken)',
                                    }}
                                    onMouseEnter={() => {
                                      setHoveredCharacterId(spec.charId);
                                      if (currentGameFields) {
                                        const specGroup = currentGameFields.find((g) => g.key === "spec");
                                        if (specGroup) {
                                          const charOpts = (spec.char as any).options ?? [];
                                          const matchingOpt = charOpts.filter((o: any) => o && Number(o.group_id) === Number(specGroup.id))
                                            .map((o: any) => specGroup.options.find((fo) => Number(fo.id) === Number(o.option_id)))
                                            .find((opt: any) => opt && opt.label?.trim() === spec.specLabel);
                                          if (matchingOpt?.metadata?.provides_tags) {
                                            setHoveredSpecTags(new Set(matchingOpt.metadata.provides_tags));
                                          } else {
                                            setHoveredSpecTags(new Set());
                                          }
                                        }
                                      }
                                    }}
                                    onMouseDown={(e: React.MouseEvent) => {
                                      if (e.button !== 0) return;
                                      e.preventDefault();
                                      setIsDragging(false);
                                      setDragOrigin({ type: "bench", charId: spec.charId, roleLabel: spec.roleLabel });
                                      setDragPosition({ x: e.clientX, y: e.clientY });
                                    }}
                                    onClick={() => {
                                      const emptyIdx = rosterSlots.indexOf(null);
                                      if (emptyIdx === -1) return;
                                  
                                      // Squad intercept check (same logic as before)
                                      const charData = charById.get(spec.charId);
                                      const charUserId = player.userId;
                                      const charSquadInfo = player.squadInfo;
                                  
                                      if (charSquadInfo && charSquadInfo.squadSize > 1) {
                                        const seatedCharIds = new Set(rosterSlots.filter(Boolean));
                                        const seatedUserIds = new Set<string>();
                                        rosterSlots.forEach((slotId) => {
                                          if (!slotId) return;
                                          const c = charById.get(slotId);
                                          const uid = c?.userId || (c as any)?.ownerId;
                                          if (uid) seatedUserIds.add(uid);
                                        });
                                    
                                        const squadMemberChars: { id: string; name: string; className: string; roles: string[]; userId: string }[] = [];
                                        benchAvailable.forEach((c: any) => {
                                          if (c.id === spec.charId) return;
                                          const uid = c.userId || c.ownerId;
                                          if (!uid || !charSquadInfo.memberUserIds.includes(uid)) return;
                                          if (seatedCharIds.has(c.id)) return;
                                          if (seatedUserIds.has(uid)) return;
                                          squadMemberChars.push({
                                            id: c.id, name: c.name,
                                            className: c.metadata?.class_name || c.className || "Unknown",
                                            roles: resolveRoleLabels(c, currentGameFields), userId: uid
                                          });
                                        });
                                    
                                        if (squadMemberChars.length > 0) {
                                          const triggerRoles = charData ? resolveRoleLabels(charData, currentGameFields) : [spec.roleLabel];
                                          setSquadIntercept({
                                            triggerCharId: spec.charId,
                                            triggerCharName: spec.charName,
                                            triggerCharClassName: spec.className,
                                            triggerCharRoles: triggerRoles,
                                            triggerCharUserId: charUserId,
                                            squadInfo: charSquadInfo,
                                            targetIndex: emptyIdx,
                                            availableSquadmates: squadMemberChars
                                          });
                                          const initialSelections: Record<string, { charId: string; roleLabel: string }> = {
                                            [charUserId]: { charId: spec.charId, roleLabel: spec.roleLabel || triggerRoles[0] || "DPS" }
                                          };
                                          squadMemberChars.forEach(sm => {
                                            if (!initialSelections[sm.userId]) {
                                              initialSelections[sm.userId] = { charId: sm.id, roleLabel: sm.roles[0] || "DPS" };
                                            }
                                          });
                                          setSquadSelections(initialSelections);
                                          setAllowSquadSplit(false);
                                          return;
                                        }
                                      }
                                  
                                      // Normal click - seat this spec
                                      setRosterSlots((prev: any[]) => { const n = [...prev]; n[emptyIdx] = spec.charId; return n; });
                                      setRosterRoles((prev: any[]) => { const n = [...prev]; n[emptyIdx] = spec.roleLabel; return n; });
                                    }}
                                  >
                                    {iconUrl && (
                                      <img
                                        src={iconUrl}
                                        alt=""
                                        className="h-full w-full object-cover"
                                        onError={(e) => { const el = e.currentTarget as HTMLImageElement; el.style.display='none'; if (el.nextElementSibling) (el.nextElementSibling as HTMLElement).style.display='flex'; }}
                                      />
                                    )}
                                    {/* Fallback when the spec has no icon configured, or the image 404s. */}
                                    <span
                                      className="absolute inset-0 flex items-center justify-center text-base"
                                      style={{ display: iconUrl ? 'none' : undefined }}
                                    >
                                      {getRoleIcon(spec.roleLabel)}
                                    </span>
                                    {hasScore && (
                                      <span
                                        className="absolute bottom-0 right-0 rounded-tl-md px-1 font-mono text-[9px] font-bold leading-[13px]"
                                        style={{ backgroundColor: 'var(--c-surface)', color: getParseColor(wclScore, onLight) }}
                                      >
                                        {wclScore}
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {filteredPlayerBench.length === 0 && benchAvailable.length > 0 && benchRoleFilter && (
                  <div className="text-center text-xs text-[var(--c-ink-4)] py-4">
                    No players with <span className="font-bold text-[var(--c-ink-3)]">{benchRoleFilter}</span> spec available.
                  </div>
                )}

                {/*
                  Greyed-out buckets. One component, five instances — these were
                  five copy-pasted blocks that had drifted apart (three printed
                  the role where two printed the spec).
                */}
                <DisabledBenchSection
                  title="Already Seated"
                  toneClass="text-[var(--c-warn-ink)]"
                  dimClass="opacity-60 grayscale hover:grayscale-0 transition-all"
                  players={buildDisabledPlayerCards(lockedByOwnerCharacters)}
                />
                <DisabledBenchSection
                  title="Raid Locked (Already Assigned This Week)"
                  toneClass="text-[var(--c-squad-ink)]"
                  dimClass="opacity-50 grayscale"
                  players={buildDisabledPlayerCards(benchLockedOut)}
                />
                <DisabledBenchSection
                  title="In Another Raid at This Time"
                  toneClass="text-[var(--c-warn-ink)]"
                  dimClass="opacity-50 grayscale"
                  players={buildDisabledPlayerCards(benchBusy)}
                />
                <DisabledBenchSection
                  title="Not Interested in This Raid"
                  toneClass="text-[var(--c-warn-ink)]"
                  dimClass="opacity-60"
                  players={buildDisabledPlayerCards(benchNotInterested)}
                />
                <DisabledBenchSection
                  title="Unavailable (Time Mismatch)"
                  toneClass="text-[var(--c-bad-ink)]"
                  dimClass="opacity-50 grayscale"
                  players={buildDisabledPlayerCards(benchUnavailable)}
                />
                </div>
              </div>
  
              {/* RIGHT: BOARD */}
            <div className="flex flex-col bg-transparent min-h-0">
              {/* Header with inline composition summary */}
              <div className="flex items-center justify-between border-b border-[var(--c-line)] p-5 bg-[var(--c-sunken)]">
                <div className="flex items-center gap-4">
                  <div className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-2)]">Raid Roster</div>
                  {/* Inline Composition Summary */}
                  {(() => {
                const roleCounts: Record<string, number> = { "Tank": 0, "Healer": 0, "Melee DPS": 0, "Ranged DPS": 0 };
                const classCounts: Record<string, number> = {};
                
                rosterSlots.forEach((charId, idx) => {
                  if (!charId) return;
                  const char = charById.get(charId);
                  if (!char) return;
                  
                  const role = rosterRoles[idx];
                  if (role && roleCounts.hasOwnProperty(role)) {
                    roleCounts[role]++;
                  }
                  
                  const className = (char as any).className || (char as any).class_name || "Unknown";
                  classCounts[className] = (classCounts[className] || 0) + 1;
                });
                
                const sortedClasses = Object.entries(classCounts).sort((a, b) => b[1] - a[1]);
                const totalAssigned = rosterSlots.filter(Boolean).length;
                
                return totalAssigned > 0 ? (
                  <div className="flex flex-wrap items-center gap-3 px-8 py-3 border-b border-[var(--c-line-soft)] bg-[var(--c-sunken)]">
                    {/* Role counts */}
                    <div className="flex items-center gap-2 text-xs">
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--c-sunken)] border border-[var(--c-line)]">
                        <span>🛡️</span><span className="font-bold text-[var(--c-ink)]">{roleCounts["Tank"]}</span>
                      </span>
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--c-sunken)] border border-[var(--c-line)]">
                        <span>💚</span><span className="font-bold text-[var(--c-ink)]">{roleCounts["Healer"]}</span>
                      </span>
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--c-sunken)] border border-[var(--c-line)]">
                        <span>⚔️</span><span className="font-bold text-[var(--c-ink)]">{roleCounts["Melee DPS"]}</span>
                      </span>
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--c-sunken)] border border-[var(--c-line)]">
                        <span>🏹</span><span className="font-bold text-[var(--c-ink)]">{roleCounts["Ranged DPS"]}</span>
                      </span>
                    </div>
                    
                    {/* Divider */}
                    {sortedClasses.length > 0 && <span className="text-[var(--c-ink-4)]">|</span>}
                    
                    {/* Class breakdown */}
                    <div className="flex flex-wrap items-center gap-1">
                      {sortedClasses.map(([className, count]) => (
                        <span 
                        key={className}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--c-surface)] border border-[var(--c-line-soft)] text-[10px]"
                        title={className}
                      >
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: onLightAccent(CLASS_COLORS[className] || '#9d9d9d') }} />
                        <span className="font-bold" style={{ color: onLight(CLASS_COLORS[className] || '#9d9d9d') }}>{count}</span>
                      </span>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}
                </div>
                <div className="rounded-full bg-[var(--c-sunken-2)] px-2 py-0.5 text-[10px] font-bold text-[var(--c-ink)]">{rosterSlots.filter(Boolean).length} / {size} Filled</div>
              </div>

              <div className="flex-1 overflow-y-auto p-8">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {chunkIntoGroups(rosterSlots, 5).map((group, gIdx) => (
                    <div key={gIdx} className="rounded-xl border border-[var(--c-line-soft)] bg-transparent p-2 shadow-[var(--c-shadow-lg)]">
                      <div className="mb-2 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-[var(--c-ink-4)] border-b border-[var(--c-line-soft)]">Group {gIdx + 1}</div>
                      <div className="space-y-1.5">
                        {group.map((charId, sIdx) => {
                          const slotIdx = (gIdx * 5) + sIdx;
                          const char = charId ? charById.get(charId) : null;
                          const role = rosterRoles[slotIdx];
                          const isDraggingFromHere = dragOrigin?.type === "slot" && dragOrigin.slotIndex === slotIdx;
                          const ownerKey = char ? getOwnerKeyForChar(char, me) : null;
                          const isHovered = char ? hoveredCharacterId === char.id : false;
                          const isSameOwner = !!(char && hoveredOwnerKey && ownerKey && hoveredOwnerKey === ownerKey && !isHovered);
                          const labelLines = char ? characterCardLines(char, currentGameFields, (char as any).ownerDisplayName) : null;
                          const roleColor = char ? (role ? getRoleColorForRoleLabel(char, currentGameFields, role) : getPrimaryRoleColorForChar(char, currentGameFields)) : null;
                          const highlightBg = (isHovered || isSameOwner) && roleColor ? softBgFromHex(roleColor, isHovered ? 0.3 : 0.15) : undefined;

                          // Get squad info for seated character
                          const charUserId = char ? ((char as any).userId || (char as any).ownerId) : null;
                          const charSquadInfo = charUserId ? squadLookup[charUserId] : null;
                          const isSquadmateHovered = !!(char && hoveredSquadId && charSquadInfo?.squadId === hoveredSquadId && hoveredCharacterId !== char.id);

                          return (
                            <RosterCard key={slotIdx} forwardRef={(el: HTMLDivElement | null) => { if (el) slotRefs.current[slotIdx] = el; }} character={char || null} labelLines={labelLines} roleColor={roleColor} ownerDiscord={char ? (char as any).ownerDisplayName : null} highlightBg={highlightBg} isSlot displayRoles={char ? (role ? [role] : resolveRoleLabels(char, currentGameFields)) : []} displaySpec={char && role ? (() => { const entries = resolveSpecEntries(char, currentGameFields); const match = entries.find(e => e.roleLabel === role); return match ? match.specLabel : entries[0]?.specLabel || null; })() : null} isDraggingFromHere={isDraggingFromHere} isSameOwner={isSameOwner} squadInfo={charSquadInfo} isSquadmateHovered={isSquadmateHovered} wclScore={char ? (char as any).metadata?.wcl_perf?.score : undefined} classIconUrl={char ? (() => { const cn = (char as any).className || (char as any).class_name; if (role) { const entries = resolveSpecEntries(char, currentGameFields); const m = entries.find(e => e.roleLabel === role); const specUrl = getSpecIconUrl(m?.specLabel || entries[0]?.specLabel, cn, currentGameFields); if (specUrl) return specUrl; } return getClassIconUrl(cn, currentGameFields); })() : null}
                              onMouseEnter={() => { if (char) { setHoveredCharacterId(char.id); if (ownerKey) setHoveredOwnerKey(ownerKey); if (charSquadInfo) setHoveredSquadId(charSquadInfo.squadId); } }}
                              onMouseLeave={() => { setHoveredCharacterId(null); setHoveredOwnerKey(null); setHoveredSquadId(null); }}
                              onMouseDown={(e: React.MouseEvent) => { if (!char || e.button !== 0) return; e.preventDefault(); setIsDragging(false); setDragOrigin({ type: "slot", charId: char.id, slotIndex: slotIdx, roleLabel: role }); setDragPosition({ x: e.clientX, y: e.clientY }); }}
                              onRemove={() => {
                                // Check if this character is in a squad
                                if (charSquadInfo && charSquadInfo.squadSize > 1) {
                                  // Remove all squad members - need to track which indices to clear
                                  const squadUserIds = new Set(charSquadInfo.memberUserIds);
                                  const indicesToClear: number[] = [];
                                  
                                  rosterSlots.forEach((id, idx) => {
                                    if (!id) return;
                                    const c = charById.get(id);
                                    const uid = c ? ((c as any).userId || (c as any).ownerId) : null;
                                    if (uid && squadUserIds.has(uid)) {
                                      indicesToClear.push(idx);
                                    }
                                  });
                                  
                                  setRosterSlots((p: any[]) => {
                                    const n = [...p];
                                    indicesToClear.forEach(idx => { n[idx] = null; });
                                    return n;
                                  });
                                  setRosterRoles((p: any[]) => {
                                    const n = [...p];
                                    indicesToClear.forEach(idx => { n[idx] = null; });
                                    return n;
                                  });
                                } else {
                                  // Just remove this slot
                                  setRosterSlots((p: any[]) => { const n = [...p]; n[slotIdx] = null; return n; });
                                  setRosterRoles((p: any[]) => { const n = [...p]; n[slotIdx] = null; return n; });
                                }
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

{/* TRUE BENCH: backup players (unbounded). Locked + bilocation-blocked like raiders, but outside the seats. */}
<div ref={benchZoneRef} className="mt-6 rounded-xl border border-dashed border-[var(--c-warn-line)] bg-[var(--c-warn-soft)] p-4">
  <div className="mb-3 flex flex-wrap items-center gap-3">
    <span className="text-xs font-bold uppercase tracking-widest text-[var(--c-warn-ink)]">Bench (Backup)</span>
    <span className="rounded-full bg-[var(--c-warn-soft)] px-2 py-0.5 text-[10px] font-bold text-[var(--c-warn-ink)]">{benchSlots.length} {benchSlots.length === 1 ? "Player" : "Players"}</span>
    <span className="text-[10px] text-[var(--c-ink-4)]">Counted like raiders for lockout &amp; bilocation, but outside the {size} seats. Drag onto a seat to promote.</span>
  </div>
  {benchSlots.length === 0 ? (
    <div className="py-6 text-center text-[11px] text-[var(--c-ink-4)]">Drag players here to add backups.</div>
  ) : (
    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {benchSlots.map((charId, bIdx) => {
        const char = charById.get(charId) || null;
        const role = benchRoles[bIdx] || null;
        const ownerKey = char ? getOwnerKeyForChar(char, me) : null;
        const isHovered = char ? hoveredCharacterId === char.id : false;
        const isSameOwner = !!(char && hoveredOwnerKey && ownerKey && hoveredOwnerKey === ownerKey && !isHovered);
        const labelLines = char ? characterCardLines(char, currentGameFields, (char as any).ownerDisplayName) : null;
        const roleColor = char ? (role ? getRoleColorForRoleLabel(char, currentGameFields, role) : getPrimaryRoleColorForChar(char, currentGameFields)) : null;
        const highlightBg = (isHovered || isSameOwner) && roleColor ? softBgFromHex(roleColor, isHovered ? 0.3 : 0.15) : undefined;
        const isDraggingFromHere = dragOrigin?.type === "benchseat" && dragOrigin.charId === charId;
        return (
          <RosterCard
            key={`bench-${charId}`}
            character={char}
            labelLines={labelLines}
            roleColor={roleColor}
            ownerDiscord={char ? (char as any).ownerDisplayName : null}
            highlightBg={highlightBg}
            isSlot
            isSameOwner={isSameOwner}
            isDraggingFromHere={isDraggingFromHere}
            displayRoles={char ? (role ? [role] : resolveRoleLabels(char, currentGameFields)) : []}
            displaySpec={char && role ? (() => { const entries = resolveSpecEntries(char, currentGameFields); const match = entries.find(e => e.roleLabel === role); return match ? match.specLabel : entries[0]?.specLabel || null; })() : null}
            wclScore={char ? (char as any).metadata?.wcl_perf?.score : undefined}
            classIconUrl={char ? (() => { const cn = (char as any).className || (char as any).class_name; if (role) { const entries = resolveSpecEntries(char, currentGameFields); const m = entries.find(e => e.roleLabel === role); const specUrl = getSpecIconUrl(m?.specLabel || entries[0]?.specLabel, cn, currentGameFields); if (specUrl) return specUrl; } return getClassIconUrl(cn, currentGameFields); })() : null}
            onMouseEnter={() => { if (char) { setHoveredCharacterId(char.id); if (ownerKey) setHoveredOwnerKey(ownerKey); } }}
            onMouseLeave={() => { setHoveredCharacterId(null); setHoveredOwnerKey(null); }}
            onMouseDown={(e: React.MouseEvent) => { if (!char || e.button !== 0) return; e.preventDefault(); setIsDragging(false); setDragOrigin({ type: "benchseat", charId: char.id, roleLabel: role }); setDragPosition({ x: e.clientX, y: e.clientY }); }}
            onRemove={() => { setBenchSlots((prev) => prev.filter((id) => id !== charId)); setBenchRoles((prev) => prev.filter((_, i) => i !== bIdx)); }}
          />
        );
      })}
    </div>
  )}
</div>

{/* Buff Coverage Panel */}
{compositionTags.length > 0 && rosterSlots.some(Boolean) && (
  <div className="mt-6 rounded-xl border border-[var(--c-line)] bg-[var(--c-sunken)] backdrop-blur-sm">
    <button
      onClick={() => setBuffPanelOpen(!buffPanelOpen)}
      className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-[var(--c-sunken)] transition-colors rounded-t-xl"
    >
      <div className="flex items-center gap-3">
        <span className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-2)]">Composition Coverage</span>
        {(() => {
          const totalCovered = buffCoverage.reduce((s, g) => s + g.coveredCount, 0);
          const totalAll = buffCoverage.reduce((s, g) => s + g.totalCount, 0);
          const pct = totalAll > 0 ? Math.round((totalCovered / totalAll) * 100) : 0;

          // Average WCL parse across seated characters (only counts those with a score)
          const seatedScores: number[] = [];
          rosterSlots.forEach((charId) => {
            if (!charId) return;
            const c = charById.get(charId);
            const score = (c as any)?.metadata?.wcl_perf?.score;
            if (typeof score === 'number' && score > 0) seatedScores.push(score);
          });
          const avgParse = seatedScores.length > 0
            ? Math.round(seatedScores.reduce((s, n) => s + n, 0) / seatedScores.length)
            : null;

          return (
            <>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${pct >= 80 ? 'bg-[var(--c-good-soft)] text-[var(--c-good-ink)]' : pct >= 50 ? 'bg-[var(--c-warn-soft)] text-[var(--c-warn-ink)]' : 'bg-[var(--c-bad-soft)] text-[var(--c-bad-ink)]'}`}>
                {totalCovered}/{totalAll} ({pct}%)
              </span>
              {avgParse !== null && (
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[var(--c-surface)] font-mono flex items-center gap-1"
                  style={{ color: getParseColor(avgParse, onLight) }}
                  title={`Average Warcraft Logs parse across ${seatedScores.length} seated character${seatedScores.length === 1 ? '' : 's'}`}
                >
                  <span className="text-[var(--c-ink-3)] font-sans">AVG PARSE</span>
                  {avgParse}
                </span>
              )}
            </>
          );
        })()}
      </div>
      <svg className={`w-4 h-4 text-[var(--c-ink-3)] transition-transform ${buffPanelOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    
    {buffPanelOpen && (
      <div className="px-5 pb-5 space-y-3 border-t border-[var(--c-line-soft)] columns-2 gap-6">
        {buffCoverage.map((group) => (
          <div key={`${group.category}-${group.subcategory || ''}`} className="pt-3 break-inside-avoid">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--c-ink-3)]">
                {group.subcategory || group.category}
              </span>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${group.coveredCount === group.totalCount ? 'bg-[var(--c-good-soft)] text-[var(--c-good-ink)]' : 'bg-[var(--c-sunken)] text-[var(--c-ink-4)]'}`}>
                {group.coveredCount}/{group.totalCount}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {group.tags.map((tc) => (
                <span
                  key={tc.tag.tag_key}
                  title={tc.covered ? `${tc.tag.description || tc.tag.tag_label}: ${tc.providers.join(', ')}` : `${tc.tag.description || tc.tag.tag_label}: MISSING`}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border transition-all ${
                    hoveredSpecTags.size > 0 && hoveredSpecTags.has(tc.tag.tag_key)
                      ? 'bg-[var(--c-accent-soft)] border-[var(--c-accent-line)] text-[var(--c-accent-ink)] ring-1 ring-[var(--c-accent-line)]'
                      : tc.covered
                        ? 'bg-[var(--c-good-soft)] border-[var(--c-good-line)] text-[var(--c-good-ink)]'
                        : 'bg-[var(--c-sunken)] border-[var(--c-line-soft)] text-[var(--c-ink-4)]'
                  }`}
                >
                  {tc.tag.tag_label}
                  {tc.covered && tc.providers.length > 1 && (
                    <span className="text-[9px] text-[var(--c-good-ink)]">{tc.providers.length}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
)}
</div>
</div>
</div>
)}

{/* --- STEP 2: REVIEW --- */}
        {step === 2 && (
          <div className="h-full overflow-y-auto p-8">
            <div className="mx-auto max-w-3xl space-y-8">
              <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] p-8 backdrop-blur-sm shadow-[var(--c-shadow-lg)]">
                <h3 className="mb-6 text-xs font-bold uppercase tracking-widest text-[var(--c-ink-3)] border-b border-[var(--c-line-soft)] pb-2">Raid Summary</h3>
                <div className="grid gap-6 md:grid-cols-3">
                  <div>
                    <div className="text-[10px] uppercase text-[var(--c-ink-3)] font-bold mb-1">Instance</div>
                    <div className="text-xl font-bold text-[var(--c-ink)]">{raidLabel || "Untitled Raid"}</div>
                    <div className="text-sm text-[var(--c-accent-ink)]">{raidOptions.find((o: any) => o.id === raidOptionId)?.label}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[var(--c-ink-3)] font-bold mb-1">Schedule</div>
                    <div className="text-xl font-bold text-[var(--c-ink)]">
                        {scheduleMode === "series" ? (
                            <span className="text-[var(--c-squad-ink)]">Series: {Object.keys(recurringPatterns).join(", ")}</span>
                        ) : (
                            <div className="flex flex-col gap-1">
                                {(scheduleMode === "single" ? [sessionList[0]] : sessionList).map((s: any, i: number) => (
                                    <span key={i} className="text-sm text-[var(--c-good-ink)]">
                                        {new Date(s.date).toLocaleString(undefined, {weekday: 'short', month:'short', day:'numeric'})} @ {s.startTime}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    {scheduleMode === "series" && <div className="text-sm text-[var(--c-ink-3)]">Recurring Weekly</div>}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[var(--c-ink-3)] font-bold mb-1">Roster</div>
                    <div className="text-xl font-bold text-[var(--c-good-ink)]">{rosterSlots.filter(Boolean).length} / {size}</div>
                    <div className="text-sm text-[var(--c-ink-3)]">Players Assigned</div>
                  </div>
                </div>
              </div>

              {/* Leadership Assignments */}
              <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] p-6 backdrop-blur-sm shadow-[var(--c-shadow-lg)]">
                <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-[var(--c-ink-3)] border-b border-[var(--c-line-soft)] pb-2">Leadership</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--c-ink-3)]">Raid Leader</label>
                    <div className="relative">
                      <select value={raidLeaderId} onChange={(e) => setRaidLeaderId(e.target.value)} className="w-full appearance-none rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-4 py-3 text-sm text-[var(--c-ink)] focus:border-[var(--c-accent)] focus:outline-none transition-all hover:bg-[var(--c-surface)]">
                        <option value="">Unassigned</option>
                        {eligibleRaidLeaders.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--c-ink-3)] text-xs">▼</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--c-ink-3)]">Master Looter</label>
                    <div className="relative">
                      <select value={lootMasterId} onChange={(e) => setLootMasterId(e.target.value)} className="w-full appearance-none rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] px-4 py-3 text-sm text-[var(--c-ink)] focus:border-[var(--c-accent)] focus:outline-none transition-all hover:bg-[var(--c-surface)]">
                        <option value="">Unassigned</option>
                        {eligibleLootMasters.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[var(--c-ink-3)] text-xs">▼</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Soft Reserve Configuration */}
              <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] p-6 backdrop-blur-sm shadow-[var(--c-shadow-lg)]">
                <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-[var(--c-ink-3)] border-b border-[var(--c-line-soft)] pb-2">Loot Rules</h3>
                <div className="space-y-4">
                  {/* SR Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-[var(--c-ink)]">Enable Soft Reserves</div>
                      <div className="text-xs text-[var(--c-ink-3)] mt-0.5">Players can reserve items they want before the raid</div>
                    </div>
                    <button
                      onClick={() => setSoftReserveEnabled(!softReserveEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${softReserveEnabled ? 'bg-[var(--c-accent)]' : 'bg-[var(--c-sunken-2)]'}`}
                    >
                      <span className={`inline-block h-4 w-4 rounded-full bg-[var(--c-surface)] shadow-[var(--c-shadow-sm)] transition-transform ${softReserveEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>

                  {/* Max Reserves (only when enabled) */}
                  {softReserveEnabled && (
                    <div className="flex items-center justify-between pl-4 border-l-2 border-[var(--c-accent-line)]">
                      <div>
                        <div className="text-sm font-medium text-[var(--c-ink)]">Reserves Per Player</div>
                        <div className="text-xs text-[var(--c-ink-3)] mt-0.5">How many items each player can soft reserve</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setMaxSoftReserves(Math.max(1, maxSoftReserves - 1))}
                          className="h-8 w-8 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] flex items-center justify-center transition-all text-lg font-bold"
                        >
                          −
                        </button>
                        <span className="w-8 text-center text-lg font-bold text-[var(--c-accent-ink)]">{maxSoftReserves}</span>
                        <button
                          onClick={() => setMaxSoftReserves(Math.min(10, maxSoftReserves + 1))}
                          className="h-8 w-8 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] flex items-center justify-center transition-all text-lg font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced Roster Composition Overview */}
              {(() => {
                // Build detailed role data with character info
                const roleData: Record<string, { count: number; characters: { id: string; name: string; className: string }[] }> = {
                  "Tank": { count: 0, characters: [] },
                  "Healer": { count: 0, characters: [] },
                  "Melee DPS": { count: 0, characters: [] },
                  "Ranged DPS": { count: 0, characters: [] }
                };
                const classCounts: Record<string, number> = {};
                
                rosterSlots.forEach((charId, idx) => {
                  if (!charId) return;
                  const char = charById.get(charId);
                  if (!char) return;
                  
                  // Count role from stored rosterRoles
                  const role = rosterRoles[idx];
                  if (role && roleData.hasOwnProperty(role)) {
                    roleData[role].count++;
                    roleData[role].characters.push({
                      id: charId,
                      name: (char as any).name || "Unknown",
                      className: (char as any).className || (char as any).class_name || "Unknown"
                    });
                  }
                  
                  // Count class
                  const className = (char as any).className || (char as any).class_name || "Unknown";
                  classCounts[className] = (classCounts[className] || 0) + 1;
                });
                
                const sortedClasses = Object.entries(classCounts).sort((a, b) => b[1] - a[1]);
                
                return (
                  <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] p-6 backdrop-blur-sm shadow-[var(--c-shadow-lg)]">
                    <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-[var(--c-ink-3)] border-b border-[var(--c-line-soft)] pb-2">Raid Composition</h3>
                    
                    {/* Role breakdown with character names */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {(["Tank", "Healer", "Melee DPS", "Ranged DPS"] as const).map(role => {
                        const data = roleData[role];
                        const icon = role === "Tank" ? "🛡️" : role === "Healer" ? "💚" : role === "Melee DPS" ? "⚔️" : "🏹";
                        const label = role === "Melee DPS" ? "Melee" : role === "Ranged DPS" ? "Ranged" : role + "s";
                        return (
                          <div key={role} className="p-3 rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line)]">
                            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-[var(--c-line-soft)]">
                              <span className="text-lg">{icon}</span>
                              <span className="text-sm font-bold text-[var(--c-ink)]">{data.count}</span>
                              <span className="text-[10px] uppercase text-[var(--c-ink-3)] font-bold">{label}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {data.characters.length > 0 ? data.characters.map(c => (
                                <span 
                                  key={c.id}
                                  className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                                  style={{ 
                                    backgroundColor: `${onLightAccent(CLASS_COLORS[c.className] || '#9d9d9d')}1f`,
                                    color: onLight(CLASS_COLORS[c.className] || '#9d9d9d')
                                  }}
                                >
                                  {c.name}
                                </span>
                              )) : (
                                <span className="text-[10px] text-[var(--c-ink-4)] italic">None</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Class breakdown */}
                    <div className="text-[10px] uppercase text-[var(--c-ink-3)] font-bold mb-2">Class Breakdown</div>
                    <div className="flex flex-wrap gap-2">
                      {sortedClasses.map(([className, count]) => (
                        <div 
                          key={className}
                          className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-[var(--c-surface)] border border-[var(--c-line)]"
                        >
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: onLightAccent(CLASS_COLORS[className] || '#9d9d9d') }} />
                          <span className="text-xs font-bold" style={{ color: onLight(CLASS_COLORS[className] || '#9d9d9d') }}>{count}</span>
                          <span className="text-xs text-[var(--c-ink-3)]">{className}</span>
                        </div>
                      ))}
                      {sortedClasses.length === 0 && (
                        <div className="text-xs text-[var(--c-ink-4)] italic">No players assigned yet</div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div className="space-y-4">
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-3)] pl-1">Raid Notes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Consumable requirements, loot rules, Discord links, boss assignments..." className="h-40 w-full rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] p-4 text-sm text-[var(--c-ink)] placeholder:text-[var(--c-ink-4)] focus:border-[var(--c-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--c-accent)] transition-all" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div className="relative z-10 flex h-20 shrink-0 items-center justify-between border-t border-[var(--c-line)] bg-[var(--c-surface)] px-8">
        <button onClick={onCancel} className="rounded-lg px-4 py-2 text-sm font-medium text-[var(--c-ink-3)] hover:text-[var(--c-ink)] hover:bg-[var(--c-sunken)] transition-colors">Cancel</button>
        <div className="flex gap-4">
          {step > 0 && <button onClick={() => setStep((s) => (s - 1) as any)} className="rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] px-6 py-2.5 text-sm font-bold text-[var(--c-ink)] hover:bg-[var(--c-sunken-2)] hover:border-[var(--c-line-strong)] transition-all">Back</button>}
          {step < 2 ? (
            <button onClick={() => setStep((s) => (s + 1) as any)} disabled={!validateStep()} className="rounded-lg bg-[var(--c-accent)] px-8 py-2.5 text-sm font-bold text-[var(--c-ink-on-fill)] shadow-[var(--c-shadow)] hover:bg-[var(--c-accent-hover)] hover:shadow-[var(--c-shadow-lg)] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transition-all">Next Step</button>
          ) : (
            <div className="flex items-center gap-6">
              {selectedGuild && (() => {
                const lockoutWeek = getRaidLockoutWeek();
                if (!lockoutWeek) return null;
                const fmtWeek = () => {
                  const d = new Date(lockoutWeek + 'T00:00:00Z');
                  const end = new Date(d); end.setUTCDate(d.getUTCDate() + 6);
                  const fmt = (dt: Date) => `${dt.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })} ${dt.getUTCDate()}`;
                  return `${fmt(d)} – ${fmt(end)}`;
                };
                return (
                  <div className="flex items-center gap-3 px-4 py-2 rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)]">
                    <button
                      type="button"
                      onClick={() => setReleaseOnCreate(prev => !prev)}
                      className={`relative w-10 h-5 rounded-full transition-colors duration-200 shrink-0 ${releaseOnCreate ? 'bg-[var(--c-warn)]' : 'bg-[var(--c-sunken-2)]'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-[var(--c-surface)] shadow-[var(--c-shadow-sm)] transition-transform duration-200 ${releaseOnCreate ? 'translate-x-5' : ''}`} />
                    </button>
                    <div className="flex flex-col">
                    <span className="text-xs font-bold text-[var(--c-ink-2)]">Release bench to PUG — {fmtWeek()}</span>
                    <span className="text-[10px] text-[var(--c-ink-4)]">Opted-in characters not assigned will be available to other raids this lockout</span>
                    </div>
                  </div>
                );
              })()}
              <button onClick={() => handleSubmit(false)} disabled={submitting} className="rounded-lg bg-[var(--c-sunken-2)] border border-[var(--c-line)] px-6 py-2.5 text-sm font-bold text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] disabled:opacity-50 disabled:cursor-not-allowed transition-all shrink-0">{submitting ? "Saving..." : "Save"}</button>
              <button onClick={() => handleSubmit(true)} disabled={submitting} className="rounded-lg bg-[var(--c-good)] px-8 py-2.5 text-sm font-bold text-[var(--c-ink-on-fill)] shadow-[var(--c-shadow)] hover:brightness-95 hover:shadow-[var(--c-shadow-lg)] disabled:opacity-50 disabled:cursor-not-allowed transition-all shrink-0">{submitting ? "Publishing..." : scheduleMode === 'series' ? "Create Series" : "Publish"}</button>
            </div>
          )}
        </div>
      </div>

      {/* SQUAD INTERCEPT MODAL */}
      {squadIntercept && (() => {
        // Group ALL characters by userId (trigger + squadmates + their alts)
        const userCharMap: Record<string, { userId: string; chars: { id: string; name: string; className: string; roles: string[] }[] }> = {};
        
        // Add trigger character first
        const triggerUserId = squadIntercept.triggerCharUserId;
        userCharMap[triggerUserId] = {
          userId: triggerUserId,
          chars: [{ id: squadIntercept.triggerCharId, name: squadIntercept.triggerCharName, className: squadIntercept.triggerCharClassName, roles: squadIntercept.triggerCharRoles }]
        };
        
        // Add trigger user's alts from bench
        benchAvailable.forEach((c: any) => {
          const uid = c.userId || c.ownerId;
          if (uid === triggerUserId && c.id !== squadIntercept.triggerCharId) {
            userCharMap[triggerUserId].chars.push({
              id: c.id,
              name: c.name,
              className: c.metadata?.class_name || c.className || "Unknown",
              roles: resolveRoleLabels(c, currentGameFields)
            });
          }
        });
        
        // Add squadmates grouped by userId (they may have multiple chars/alts too)
        squadIntercept.availableSquadmates.forEach(sm => {
          if (sm.userId === triggerUserId) return; // Skip - already handled above
          if (!userCharMap[sm.userId]) {
            userCharMap[sm.userId] = { userId: sm.userId, chars: [] };
          }
          // Only add if not already in the list (avoid duplicates)
          if (!userCharMap[sm.userId].chars.some(c => c.id === sm.id)) {
            userCharMap[sm.userId].chars.push({ id: sm.id, name: sm.name, className: sm.className, roles: sm.roles });
          }
        });
        
        // Maintain order: trigger user first, then other squad members
        const allUsers = [userCharMap[triggerUserId], ...Object.values(userCharMap).filter(u => u.userId !== triggerUserId)];
        const totalSquadMembers = allUsers.length;
        const selectedCount = Object.keys(squadSelections).length;
        const allSelected = selectedCount === totalSquadMembers;
        
        return (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-[var(--c-surface)] backdrop-blur-sm animate-in fade-in" onClick={() => { setSquadIntercept(null); setSquadSelections({}); setAllowSquadSplit(false); }} />
            <div className="relative w-full max-w-md bg-[var(--c-sunken)] border border-[var(--c-squad-line)] rounded-xl shadow-[var(--c-shadow-lg)] p-6 animate-in zoom-in-95">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-[var(--c-squad-soft)] border border-[var(--c-squad-line)] flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[var(--c-squad-ink)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-[var(--c-ink)]">Seat Squad</h3>
                  <p className="text-sm text-[var(--c-squad-ink)]">{squadIntercept.squadInfo.squadName}</p>
                </div>
                <button
                  onClick={() => {
                    if (!allowSquadSplit) {
                      setAllowSquadSplit(true);
                    } else {
                      const reselect: Record<string, { charId: string; roleLabel: string }> = {};
                      allUsers.forEach(u => {
                        const firstChar = u.chars[0];
                        if (firstChar) {
                          reselect[u.userId] = { charId: firstChar.id, roleLabel: firstChar.roles[0] || "DPS" };
                        }
                      });
                      setSquadSelections(reselect);
                      setAllowSquadSplit(false);
                    }
                  }}
                  className={`text-[10px] font-bold uppercase px-3 py-1.5 rounded-lg border transition-all ${
                    allowSquadSplit
                      ? 'bg-[var(--c-warn-soft)] border-[var(--c-warn-line)] text-[var(--c-warn-ink)] hover:bg-[var(--c-warn-soft)]'
                      : 'bg-[var(--c-sunken)] border-[var(--c-line-strong)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink-2)]'
                  }`}
                >
                  {allowSquadSplit ? "Re-link All" : "Split Squad"}
                </button>
              </div>
              
              {/* Warning when split mode enabled */}
              {allowSquadSplit && (
                <div className="mb-4 p-3 rounded-lg bg-[var(--c-warn-soft)] border border-[var(--c-warn-line)] text-xs text-[var(--c-warn-ink)]">
                  <strong className="text-[var(--c-warn-ink)]">Heads up:</strong> Squads signed up together because they want to raid together. Splitting them may cause friction or no-shows. Only exclude members if absolutely necessary for comp.
                </div>
              )}
              
              {/* Squad member cards */}
              <div className="space-y-2 mb-4 max-h-[350px] overflow-y-auto">
                {allUsers.map((user, userIdx) => {
                  const sel = squadSelections[user.userId];
                  const isSelected = !!sel;
                  const selectedCharId = sel?.charId || user.chars[0]?.id;
                  const selectedChar = user.chars.find(c => c.id === selectedCharId) || user.chars[0];
                  const selectedRole = sel?.roleLabel || selectedChar?.roles[0] || "DPS";
                  const hasMultipleChars = user.chars.length > 1;
                  
                  return (
                    <div 
                      key={user.userId}
                      className={`p-3 rounded-lg border transition-all ${isSelected ? 'bg-[var(--c-squad-soft)] border-[var(--c-squad-line)]' : 'bg-[var(--c-sunken)] border-[var(--c-line)] opacity-50'}`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Checkbox - only functional in split mode */}
                        <button
                          onClick={() => {
                            if (!allowSquadSplit) return; // Can't deselect unless split mode
                            if (isSelected) {
                              setSquadSelections(prev => { const n = {...prev}; delete n[user.userId]; return n; });
                            } else {
                              const firstChar = user.chars[0];
                              setSquadSelections(prev => ({...prev, [user.userId]: { charId: firstChar.id, roleLabel: firstChar.roles[0] || "DPS" }}));
                            }
                          }}
                          disabled={!allowSquadSplit}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                            isSelected ? 'bg-[var(--c-squad)] border-[var(--c-squad)]' : 'border-[var(--c-line-strong)]'
                          } ${!allowSquadSplit ? 'cursor-default' : 'hover:border-[var(--c-line-strong)]'}`}
                        >
                          {isSelected && <svg className="w-4 h-4 text-[var(--c-ink)]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                        </button>
                        
                        <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: onLightAccent(CLASS_COLORS[selectedChar?.className || ''] || '#9d9d9d') }} />
                        
                        {/* Character selector or static display */}
                        <div className="flex-1 min-w-0">
                          {hasMultipleChars && isSelected ? (
                            <select
                              value={selectedCharId}
                              onChange={(e) => {
                                const newChar = user.chars.find(c => c.id === e.target.value);
                                setSquadSelections(prev => ({
                                  ...prev,
                                  [user.userId]: { charId: e.target.value, roleLabel: newChar?.roles[0] || "DPS" }
                                }));
                              }}
                              className="w-full bg-[var(--c-surface)] border border-[var(--c-line)] rounded px-2 py-1 text-sm font-bold text-[var(--c-ink)] focus:outline-none focus:border-[var(--c-squad)]"
                            >
                              {user.chars.map(c => (
                                <option key={c.id} value={c.id}>{c.name} ({c.className})</option>
                              ))}
                            </select>
                          ) : (
                            <>
                              <div className="font-bold text-[var(--c-ink)] text-sm truncate">{selectedChar?.name}</div>
                              <div className="text-xs text-[var(--c-ink-3)]">{selectedChar?.className}</div>
                            </>
                          )}
                        </div>
                        
                        {/* Spec pills */}
                        {isSelected && selectedChar && selectedChar.roles.length > 1 && (() => {
                          const charData = charById.get(selectedChar.id);
                          const specEntries = charData ? resolveSpecEntries(charData, currentGameFields) : [];
                          if (specEntries.length <= 1) return null;
                          return (
                            <div className="flex gap-1">
                              {specEntries.map((entry) => (
                                  <button
                                    key={entry.roleLabel}
                                    onClick={() => setSquadSelections(prev => ({...prev, [user.userId]: { ...prev[user.userId], roleLabel: entry.roleLabel }}))}
                                    className={`px-2 py-1 rounded text-[10px] font-bold transition-all ${selectedRole === entry.roleLabel ? 'bg-[var(--c-squad)] text-[var(--c-ink-on-fill)]' : 'bg-[var(--c-sunken-2)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)]'}`}
                                  >
                                    {entry.specLabel}
                                  </button>
                              ))}
                            </div>
                          );
                        })()}
                        
                        {isSelected && selectedChar && selectedChar.roles.length === 1 && (() => {
                          const charData = charById.get(selectedChar.id);
                          const specEntries = charData ? resolveSpecEntries(charData, currentGameFields) : [];
                          const specLabel = specEntries[0]?.specLabel || selectedChar.roles[0];
                          return <span className="px-2 py-1 rounded text-[10px] font-bold bg-[var(--c-squad-soft)] text-[var(--c-squad-ink)]">{specLabel}</span>;
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Action button */}
              <button
                onClick={handleSeatSquad}
                disabled={selectedCount === 0 || (!allowSquadSplit && !allSelected)}
                className="w-full py-3 rounded-lg bg-[var(--c-squad)] hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed text-[var(--c-ink-on-fill)] font-bold transition-all"
              >
                Seat {selectedCount} Player{selectedCount !== 1 ? 's' : ''}
              </button>
              
              {/* Validation message when trying to seat partial without split mode */}
              {!allowSquadSplit && !allSelected && selectedCount > 0 && (
                <div className="mt-2 text-center text-xs text-[var(--c-warn-ink)]">
                  Click the chain icon above to seat partial squad
                </div>
              )}
              
              <button 
                onClick={() => { setSquadIntercept(null); setSquadSelections({}); setAllowSquadSplit(false); }}
                className="w-full mt-2 py-2 text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        );
      })()}

      {/* DRAG GHOST */}
      {dragOrigin && dragPosition && rootRef.current && (
        <div className="pointer-events-none fixed z-[9999]" style={(() => { const rect = rootRef.current.getBoundingClientRect(); const left = dragPosition.x - rect.left; const top = dragPosition.y - rect.top; return { position: 'absolute', left, top, transform: 'translate(-50%, -50%)', width: 240, height: 56 }; })()}>
          {(() => {
            let char: any = null;
            let roles: string[] = [];
            if (dragOrigin.type === "bench" || dragOrigin.type === "benchseat") { char = charById.get(dragOrigin.charId); if (dragOrigin.roleLabel) roles = [dragOrigin.roleLabel]; } 
            else { const sid = rosterSlots[dragOrigin.slotIndex]; if (sid) char = charById.get(sid); const r = rosterRoles[dragOrigin.slotIndex]; if (r) roles = [r]; }
            const roleColor = char ? getRoleColorForRoleLabel(char, currentGameFields, roles[0]) : null;
            const labelLines = char ? characterCardLines(char, currentGameFields, (char as any).ownerDisplayName) : null;
            return <div className="rotate-2 scale-105 opacity-95 shadow-[var(--c-shadow-lg)] drop-shadow-xl"><RosterCard character={char} labelLines={labelLines} roleColor={roleColor} displayRoles={roles} isDisabled={false} isDragging={false} wclScore={char ? (char as any).metadata?.wcl_perf?.score : undefined} /></div>;
          })()}
        </div>
      )}
    </div>
  );
}

/*
 * Public entry point. The provider must sit ABOVE the page body so that
 * CreateRaidPageInner and RosterCard can both read the active palette.
 */
export default function CreateRaidPage(props: CreateRaidPageProps) {
  return (
    <ThemeProvider>
      <CreateRaidPageInner {...props} />
    </ThemeProvider>
  );
}
